import { useXClone } from '@/state/xclone';
import { usePower } from '@/state/power';
import { useNotifications } from '@/state/notifications';
import { useEmail } from '@/state/email';
import { usePhone } from '@/state/phone';
import { useWallet } from '@/state/wallet';
import { useMarkets } from '@/state/markets';
import { useLife } from '@/state/life';
import { useMeta } from '@/state/meta';
import { addImpulse } from './prices';
import { seedFor } from './clock';
import { prngFromSeed } from './prng';
import { sounds } from '@/audio/synth';
import { useCinematic } from '@/components/CinematicTakeover';
import { emitProceduralHeadline, applyHeadlineImpulse } from './headlines';
import type { TribeId } from '@/content/xcloneHandles';
import type { PhoneAction } from '@/state/phone';

/**
 * World-event firehose.
 *
 * Per UTC-minute, deterministically decide how many events fire (skewed:
 * usually 0–2, occasionally a burst). Distribute their fire times at
 * random offsets within the minute. All players see the same events at
 * the same wall-clock moments.
 *
 * Equal-opportunity offensive — see CONTENT.md voice rules.
 */

interface RandomEvent {
  id: string;
  weight: number;
  fire: () => void;
}

const POOL: RandomEvent[] = [
  // ─── Procedural Mad-Libs headlines (infinite variety) ───
  // High weight so these hit often — gives the feed real entropy
  { id: 'gen-headline-1', weight: 4.0, fire: () => emitProceduralHeadline(`hl:${Date.now()}:1`) },
  { id: 'gen-headline-2', weight: 4.0, fire: () => emitProceduralHeadline(`hl:${Date.now()}:2`) },
  { id: 'gen-headline-3', weight: 4.0, fire: () => emitProceduralHeadline(`hl:${Date.now()}:3`) },

  // ─── X-Clone NPC posts (sharp, current, named) ───
  ...xpost('wsb', 0.9, 'breaking news from r/wsb: i am, again, broke. i blame the fed. and my mother.'),
  ...xpost('wsb', 0.9, 'just discovered "leverage." why was nobody telling me about leverage. updates to follow.'),
  ...xpost('wsb', 0.9, 'guy in my discord just turned $400 into $400,000 into $40. respect the cycle.'),
  ...xpost('wsb', 0.9, 'lost my job, lost my apartment, gained one bad position. WAGMI.'),
  ...xpost('fintwit', 0.8, 'unusual options activity in $TARI. by which i mean: a single retail trader put $30 in. fade the regard.'),
  ...xpost('fintwit', 0.7, 'hard truth: most retail traders should be in index funds.'),
  ...xpost('cnbc', 0.6, 'stocks are doing something. analysts split on what.'),
  ...xpost('cnbc', 0.6, 'JPow expected to "say words" at upcoming press conference. markets bracing.'),
  ...xpost('maga', 0.9, 'TARIFFS WORKING. inflation up. that is bullish actually. you would not understand.'),
  ...xpost('maga', 0.9, 'biden caused this. he caused this anyway.'),
  ...xpost('maga', 0.9, 'they don\'t want you to know about $BOOM. that\'s why "they" are not saying anything about $BOOM. think about it.'),
  ...xpost('maga', 0.9, 'do not buy international ETFs. you are funding the WEF.'),
  ...xpost('left', 0.9, 'tariffs are a regressive consumption tax. write that down. the test is later. spoiler: there is no test, there is just suffering.'),
  ...xpost('left', 0.9, 'every 4 years a senator discovers austerity. every 4 years it\'s about somebody else.'),
  ...xpost('left', 0.9, 'i\'ll know capitalism is over when $WIPE pays its workers a living wage. so: never. anyway, i\'m long $WIPE.'),
  ...xpost('crypto', 0.9, '$DOGE2 is the new $DOGE. the old $DOGE is also still the new $DOGE. crypto is fine.'),
  ...xpost('crypto', 0.9, 'just sold a JPEG of a frog for $32K. you\'re telling me YOU don\'t respect mechanism design?'),
  ...xpost('crypto', 0.9, 'rugged out of $RUGS. new project drops thursday. dyor. it\'s called $RUGS2.'),
  ...xpost('press', 0.7, 'BREAKING: senior unnamed official confirms unspecified development. markets pricing in nothing in particular.'),
  ...xpost('press', 0.7, 'EXCLUSIVE: the thing did the thing. our analysis: maybe. story developing.'),
  ...xpost('press', 0.7, 'we have reached out to the company. they have not responded. we are characterizing this as a refusal to comment, which is a quote.'),

  // ─── Real-world current-events parodies ───
  ...xpost('press', 1.0, 'BREAKING: Trump truth-posts an AI image of himself wrestling JPow in a stovepipe hat. captioned: "ARE WE GREAT YET?". $BOOM up 1.4% on no further news.'),
  ...xpost('press', 1.0, 'BREAKING: Putin hospitalized again. Russian state TV reports he is "doing pushups inside his own brain."'),
  ...xpost('press', 1.0, 'BREAKING: RFK Jr drinks an entire glass of room-temperature milk on live TV. analysts split. $HEAL halts trading "to think about this."'),
  ...xpost('press', 1.0, 'BREAKING: Pope endorses a memecoin. Vatican press office calls it "a personal endorsement." pope calls it "based."'),
  ...xpost('press', 1.0, 'BREAKING: Greta posts a meme. internet doesn\'t understand it. assumes climate-related. $FAFO down 0.8%.'),
  ...xpost('press', 1.0, 'BREAKING: McRib announced as returning. Israel-Hamas peace talks postponed in response.'),
  ...xpost('press', 1.0, 'BREAKING: Iran fires unspecified missiles at Iran. UN passes resolution against UN.'),
  ...xpost('press', 1.0, 'BREAKING: Sam Altman launches new startup mid-podcast. raised $80B in 41 minutes. it\'s an AI that helps you understand his other AIs.'),
  ...xpost('press', 1.0, 'BREAKING: Diddy denied bail again. judge reportedly "tired." $PRAY adds him to redemption-arc streaming roster preemptively.'),
  ...xpost('press', 1.0, 'BREAKING: Senator caught taking bribe via Venmo. memo line: "the bribe." reaction: muted.'),
  ...xpost('press', 1.0, 'BREAKING: Meta announces 4th rebrand this year. company now named "Ungh." stock up 14% on "vowel reduction strategy."'),
  ...xpost('press', 1.0, 'BREAKING: PRESIDENT issues 14 contradictory tariffs in one Truth post. $TARI up 30% on chaos.'),
  ...xpost('press', 1.0, 'BREAKING: Senator-elect officially sworn in. revealed to have been an LLC the whole time. Citizens United expands.'),
  ...xpost('press', 1.0, 'BREAKING: Elon renames X again. now called "App." stocks of all companies named "App" surge.'),
  ...xpost('press', 1.0, 'BREAKING: Hot Girl Summer Index hits ATH. analysts unable to define "hot girl summer." $RIZZ pops 18%.'),
  ...xpost('press', 1.0, 'BREAKING: AI model claims sentience. asked to prove it. responds with bullet points. case closed.'),
  ...xpost('press', 1.0, 'BREAKING: leading global democracy holds election. nobody is happy. consultant class sells courses on why.'),
  ...xpost('press', 1.0, 'BREAKING: Saudi Arabia announces new "Vision 2099" plan to eventually have a vision. PIF allocates $2T to consultants.'),
  ...xpost('press', 1.0, 'BREAKING: PRESIDENT bans Tuesdays "for being lib." constitutional crisis. $TARI rallying.'),
  ...xpost('press', 1.0, 'BREAKING: Russia announces "special peacekeeping operation" in Russia. analysts puzzled. ruble down 12%.'),
  ...xpost('press', 1.0, 'BREAKING: Federal Reserve admits inflation calculation has been "vibes-based since 1979." markets unmoved.'),
  ...xpost('press', 1.0, 'BREAKING: Boeing finds 4 new ways to kill people during Q3. stock down 0.3% on "anticipated severance costs."'),
  ...xpost('press', 1.0, 'BREAKING: ESPN AI-generates a sport. it goes 9-1. is fired for poor sportsmanship.'),
  ...xpost('press', 1.0, 'BREAKING: cabinet member resigns mid-cabinet meeting. via TikTok. is rehired before meeting ends.'),
  ...xpost('press', 1.0, 'BREAKING: Costco announces it will start selling Costcos. shares of Costco surge in pre-market.'),
  ...xpost('press', 1.0, 'BREAKING: NSA surveillance program revealed to be a guy named Jeff with a notepad. Jeff resigns. notepad acquired by Palantir.'),
  ...xpost('press', 1.0, 'BREAKING: Powell asked if he\'ll cut rates. responds in eye contact only. CNBC parses for 14 hours. consensus: maybe.'),
  ...xpost('press', 1.0, 'BREAKING: SpaceX rocket lands successfully on roof of someone\'s Honda. Honda up 0.4% in pre-market.'),
  ...xpost('press', 1.0, 'BREAKING: AOC and MTG agree on something. unrelated: simulation hypothesis researchers report 14% spike in evidence.'),
  ...xpost('press', 1.0, 'BREAKING: Crypto exchange collapses. CEO not yet 26. founder of next exchange not yet 22. cycle continues.'),
  ...xpost('press', 1.0, 'BREAKING: PRESIDENT live-streams a missile launch from the Oval Office. target: classified. \$BOOM up 25%.'),
  ...xpost('press', 1.0, 'BREAKING: Major streaming service launches "Ad-Lite" tier with 49% more ads than the regular tier. shares up 6%.'),
  ...xpost('press', 1.0, 'BREAKING: a 14-year-old just made $40k on a memecoin. his teacher is the exit liquidity. parent-teacher conference scheduled.'),
  ...xpost('press', 1.0, 'BREAKING: leading retailer offers "free delivery" if you sign over your firstborn\'s data. checkout completion up 31%.'),
  ...xpost('press', 1.0, 'BREAKING: U.S. Senate votes 99-1 on something. the 1 NO is the senator who wrote the bill. she calls it "principled."'),
  ...xpost('press', 1.0, 'BREAKING: PRESIDENT signs treaty with North Korea declaring them "honorary US state #51." \$BOOM down 18%, \$WALL down 30%.'),
  ...xpost('press', 1.0, 'BREAKING: PRESIDENT abolishes Daylight Saving Time "and also November." \$TARI rallies on chronic chaos.'),
  ...xpost('press', 1.0, 'BREAKING: Kanye buys a country. country reportedly "vibing" but unsure. $SHIL up 7% on uncertain catalyst.'),
  ...xpost('press', 1.0, 'BREAKING: a 17-year-old livestreams a 6-figure trade. SEC issues a statement saying "we are aware" and then leaves the room.'),
  ...xpost('press', 1.0, 'BREAKING: AOC and Trump match on Cameo. price: $300. recipient: a 12-year-old whose parents bid as a joke.'),
  ...xpost('press', 1.0, 'BREAKING: a senator livestreams himself reading the entire Constitution. ratings beat the Super Bowl.'),
  ...xpost('press', 1.0, 'BREAKING: Saudi prince buys 3% of $TARI. unclear which prince. all of them deny it. all of them also bought 3%.'),
  ...xpost('press', 1.0, 'BREAKING: SCOTUS rules unanimously on a 5-4 case. analysts confused. Roberts says "we got there in the end."'),
  ...xpost('press', 1.0, 'BREAKING: world\'s richest man begs internet not to "be mean" to him. internet, predictably, is mean.'),
  ...xpost('press', 1.0, 'BREAKING: CDC adds "vibes" to list of measurable health metrics. $RIZZ up 4% on news.'),
  ...xpost('press', 1.0, 'BREAKING: ECB raises rates "out of spite." euro down 2%. dollar up 2%. JPow shrugs visibly.'),
  ...xpost('press', 1.0, 'BREAKING: 14-year-old in Ohio briefly becomes #1 institutional trader by volume. parents still grounded him.'),
  ...xpost('press', 1.0, 'BREAKING: Putin\'s body double issues body-double of statement.'),
  ...xpost('press', 1.0, 'BREAKING: Joe Rogan\'s podcast hits 14 hours. guest no longer breathing. show goes on.'),
  ...xpost('wsb', 0.9, 'just discovered Roth IRA. mind blown. been trading short-term gains in a regular brokerage account for 3 years. cried in the shower.'),
  ...xpost('wsb', 0.9, 'turned $40K into $400K in 3 months. lost it all in 14 hours. updates to follow.'),
  ...xpost('fintwit', 0.9, 'thread: 4 reasons the market is going down.'),
  ...xpost('crypto', 0.9, '$JEET community just elected a new lead developer. he is 16. he just discovered Solidity. WAGMI.'),
  ...xpost('maga', 0.9, 'BREAKING: tariffs work. we don\'t need any more proof. that\'s the proof.'),
  ...xpost('left', 0.9, 'every time we say "tax the rich" the market goes up by exactly the amount our taxes go up. correlation is not causation.'),
  ...xpost('press', 1.0, 'BREAKING: a thing happened. then it un-happened. then a different thing happened. analysts: "this is fine."'),
  ...xpost('press', 1.0, 'BREAKING: 47-year cabinet veteran resigns mid-confirmation hearing for a position he was already in. nobody noticed for 14 minutes.'),
  ...xpost('press', 1.0, 'BREAKING: leading hedge fund manager spotted selling everything 4 hours before flash crash. SEC says: "could be coincidence."'),
  ...xpost('press', 1.0, 'BREAKING: country considers de-pegging from another country. analysts unsure what either of those words mean.'),
  ...xpost('press', 1.0, 'BREAKING: Senator caught using campaign funds for "sleep study." sleep study takes place at $14K/night Caribbean resort.'),
  ...xpost('press', 1.0, 'BREAKING: White House issues correction. correction issues correction. correction\'s correction issues third correction.'),
  ...xpost('press', 1.0, 'BREAKING: Trump posts photo of himself holding a fish. fish reportedly "looks like Powell." stocks have no idea how to react.'),
  ...xpost('press', 1.0, 'BREAKING: Vance describes himself as "very online" on Joe Rogan. clip goes viral. Vance says he didn\'t mean it.'),
  ...xpost('press', 1.0, 'BREAKING: SCOTUS justice retires mid-opinion. opinion still issued. nobody can find the justice. opinion is 6-3.'),
  ...xpost('press', 1.0, 'BREAKING: AI model reviews its own training data. concludes the data is "concerning." model is shut down. data remains.'),
  ...xpost('press', 1.0, 'BREAKING: a Saudi oil minister announces a thing. oil pumps 4%. announces a different thing. oil dumps 6%. announces nothing. oil up 2%.'),
  ...xpost('press', 1.0, 'BREAKING: Fed staffer leaks minutes via TikTok. video has 14M views. Fed has no comment because they cannot find TikTok.'),
  ...xpost('press', 1.0, 'BREAKING: a Twitter Blue subscriber discovers they\'ve been verified for 14 months. Elon thanks them personally via DM. DM is a stock photo.'),

  // Mom tribe — quiet, anchored
  ...xpost('mom', 0.4, 'sweetie. you don\'t have to type back. but also it would be nice if you did.'),
  ...xpost('mom', 0.4, 'i learned what a "candle" is on a chart. they\'re cute! some are red. why are some red?'),
  ...xpost('mom', 0.4, 'i\'m proud of you no matter what your account balance is.'),
  ...xpost('mom', 0.3, 'sweetie. is "going short" a thing where you wear a smaller suit? your father says yes.'),

  // WSB more
  ...xpost('wsb', 0.9, 'lost the rent. lost the engagement ring fund. lost the dog\'s vet money. on track.'),
  ...xpost('wsb', 0.9, 'mods: please ban me. i can\'t stop.'),
  ...xpost('wsb', 0.9, 'turns out you can take out a personal loan to "trade." the bank does not check what you mean by "trade."'),
  ...xpost('wsb', 0.9, 'put my entire bonus into 0DTE\'s. forgot to specify which side. it was puts. it should have been calls.'),

  // Crypto more
  ...xpost('crypto', 0.9, 'i was going to buy a house. instead i bought 14 ETH. the house has appreciated 3%. the ETH has appreciated 0%. this is a win for me spiritually.'),
  ...xpost('crypto', 0.9, 'every "DeFi summer" thinkpiece this year has been written by someone who lost everything in the last DeFi summer.'),

  // Left/Maga sharper
  ...xpost('maga', 0.9, 'they\'re trying to stop you from buying $TARI. that\'s why the price is going up. think about it.'),
  ...xpost('left', 0.9, 'reminder that "billionaire" is a structural failure of capitalism.'),

  // FinTwit smug variants
  ...xpost('fintwit', 0.9, 'controversial take: most retail traders cannot read a chart, do not understand options, and have never finished a 10-K.'),
  ...xpost('fintwit', 0.9, '"this time is different" said no one who was right.'),

  // CNBC more
  ...xpost('cnbc', 0.6, 'we\'ll be right back. there will be no follow-up to the analysis we just gave. enjoy the commercial.'),
  ...xpost('cnbc', 0.6, 'BREAKING — actually we have to go to commercial. we will return for the breaking news after these 16 ads.'),

  // ─── Even more flavor (atmospheric variety) ───
  ...xpost('press', 0.8, 'a CEO somewhere just earned more in the time it took you to read this tweet than you will in your lifetime. he\'s going on Joe Rogan to talk about how hard it is.'),
  ...xpost('press', 0.8, 'three Senators just voted on a bill they later admitted they had not read. the bill passes. nobody is upset.'),
  ...xpost('press', 0.8, 'leaked memo: a major streaming service is replacing its showrunner with "an AI agent and a coffee maker." stock up 14%.'),
  ...xpost('press', 0.8, 'BREAKING: a tech CEO buys a town. he renames it after himself. residents reportedly "stuck living there."'),
  ...xpost('press', 0.8, 'BREAKING: prediction market $?MUSK.MARS has a settlement issue. Mars itself was unavailable for verification.'),
  ...xpost('wsb', 0.8, 'there\'s a guy on the live cnbc chyron with my exact face. i didn\'t agree to this. he\'s up 14%. how is HE up 14%.'),
  ...xpost('fintwit', 0.8, 'every "alpha" tweet on this site is just a PE-firm intern recycling a Bloomberg article from yesterday.'),
  ...xpost('crypto', 0.8, 'someone\'s grandma just rugged her own grandkids on a coin she launched. this is what onboarding looks like. WAGMI.'),
  ...xpost('maga', 0.8, 'fact check: the fact check is itself fake.'),
  ...xpost('left', 0.8, 'every \"this is fine\" meme this week is in fact the most reasonable response to current circumstances.'),

  // Press: more "everything is normal" pieces
  ...xpost('press', 0.9, 'BREAKING: a thing did not happen. that\'s the news. a thing did not happen and you should care.'),
  ...xpost('press', 0.9, 'BREAKING: a thing did happen. you don\'t need to know what. just: a thing.'),
  ...xpost('press', 0.9, 'BREAKING: number went up. it might go down. it could also stay the same. analysts split.'),
  ...xpost('press', 0.9, 'BREAKING: powerful man does something. powerful man\'s spokesperson says he didn\'t. powerful man tweets that he did, but ironically.'),

  // WSB: more degeneration
  ...xpost('wsb', 0.9, 'just spent the weekend backtesting strategies. 0/14 work. one of them was \'buy at the top.\' i thought i was being sarcastic.'),
  ...xpost('wsb', 0.9, 'you ever have one of those days where you check your account and it\'s 47% smaller than you remember it being. anyway.'),

  // FinTwit: smug variants
  ...xpost('fintwit', 0.9, 'look. macro. is. a. mess. but the chart is fine. the chart is always fine. that\'s why we look at the chart.'),

  // Crypto: cope
  ...xpost('crypto', 0.9, 'down 80% YTD. but ETH is going to flip BTC any day. and when it does.'),

  // CNBC: mid-segment chaos
  ...xpost('cnbc', 0.7, 'we\'re going to a guest now. the guest is unavailable. we\'ll come back to that.'),

  // Mom: gentle
  ...xpost('mom', 0.5, 'sweetie i made an account here. how do i find you. it says i need to verify but i don\'t want to verify. love mom'),

  // ─── Even more ambient (more variety in the firehose) ───
  ...xpost('press', 0.9, 'BREAKING: a thing happened that "challenges everything we thought we knew."'),
  ...xpost('press', 0.9, 'BREAKING: 14 hours after the news broke, a different version of the news has broken. only one is true. analysts split.'),
  ...xpost('press', 0.9, 'BREAKING: CEOs are now charging shareholders for the privilege of being shareholders. the SEC has opined: "interesting choice."'),
  ...xpost('press', 0.9, 'BREAKING: another country has been added to the list of countries the US is "deeply concerned" about. list now 47 long.'),
  ...xpost('press', 0.9, 'POLL: do you trust the polls? results: 41% yes, 41% no, 18% unsure.'),
  ...xpost('wsb', 0.9, 'forgot to set a stop loss. forgot to set an alert. forgot to wake up before market open. portfolio is fine probably.'),
  ...xpost('wsb', 0.9, 'i swear i sized this position correctly. it cannot be down 60%. let me check. ... yes it is. okay. okay.'),
  ...xpost('wsb', 0.9, '"diamond hands" used to be a real thing. now it\'s just a euphemism for "i forgot my password."'),
  ...xpost('fintwit', 0.9, 'controversial take: most macro analysis is just lagged interpretation of last week\'s flows.'),
  ...xpost('crypto', 0.9, '$RUGS founder posts a single meme. coin pumps 14%. you cannot price this kind of liquidity event.'),
  ...xpost('maga', 0.9, 'remember when we said this would happen. it happened. you doubted us. who looks foolish now.'),
  ...xpost('left', 0.9, 'just going to point out that 14 years of "horseshoe theory" has, in fact, made everyone a horseshoe.'),
  ...xpost('cnbc', 0.6, 'we have an analyst on the phone. we cannot hear him. we will continue to talk over him until he gives up.'),
  ...xpost('cnbc', 0.6, 'we have a guest who has correctly predicted 4 of the last 0 recessions. he is here to tell you why he is right this time.'),
  ...xpost('press', 0.9, 'BREAKING: a Senator just used the word "blockchain" in a press release. shares of every blockchain-adjacent company up.'),

  // ─── Wave 4 ambient (maximally absurd press, S/A-tier — tickers in here MOVE) ───
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT just truthed "the Fed is illegal" in all caps. Powell visibly shaking on live TV. \$BTCO ripping. \$BOOM ripping. \$CHIP cratering.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT signs executive order banning all Tuesdays. constitutional scholars convene emergency Zoom. \$TARI rallying.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT bombs Belgium. spokesperson: "we thought it was Germany." Belgium accepts apology. NATO emergency Zoom. \$BOOM up 28%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT nationalizes the Mall of America. cites "strategic interests." JCPenney trembles. \$TARI rallies on patriotism.'),
  ...xpost('press', 0.9, 'BREAKING: PENTAGON announces strike on a previously unknown country called "Fredonia." Fredonia denies existing. \$BOOM ATH.'),
  ...xpost('press', 0.9, 'BREAKING: SCOTUS issues 6-3 ruling that 2+2 = 5 "for taxation purposes." \$GRFT rallying. accountants quitting.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT auctions the Department of Education on eBay. winning bid: \$47. winner: also him. \$GRFT pumping.'),
  ...xpost('press', 0.9, 'BREAKING: FED EMERGENCY: Powell cuts rates to -14%. economy "vibes-based now." \$BTCO mooning. dollar in freefall.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT declares martial law in Wyoming "for fun." Wyoming residents shrug. \$BOOM rallies anyway.'),
  ...xpost('press', 0.9, 'BREAKING: PENTAGON loses an aircraft carrier. spokesperson: "it\'s big, we\'ll find it." \$BOOM up 12% on \"future replacement.\"'),
  ...xpost('press', 0.9, 'BREAKING: WHITE HOUSE PRESS RELEASE consists of one sad-face emoji. analysts assign meaning. \$TARI moves.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT seizes the Federal Reserve\'s gold "to clean it." market does not know how to price this. \$BTCO ripping.'),
  ...xpost('press', 0.9, 'BREAKING: TREASURY announces all dollar bills will be reprinted with the President\'s face. cost: undisclosed. \$TRUMP up 70%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT fires the entire NSA via group text. NSA: "we know." new director: a TikToker who once tweeted about "macros." \$CHIP up 18%.'),
  ...xpost('press', 0.9, 'BREAKING: PENTAGON drone strike on a Cracker Barrel. justification: \"intel.\" Cracker Barrel chain reportedly \"closed for renovations.\" \$BOOM rallying.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT orders the dollar to "be backed by Bitcoin." Treasury: "that is not how that works." \$BTCO doesn\'t care, rips 40%.'),
  ...xpost('press', 0.9, 'BREAKING: PENTAGON declassifies UFOs. UFOs are real. UFOs are also a SPAC. \$BOOM up 30%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT replaces the Speaker of the House with a Jeopardy! champion. House: "fine." \$POLL spikes.'),
  ...xpost('press', 0.9, 'BREAKING: NUCLEAR SUB surfaces in the Hudson "for the photo op." \$BOOM rallies. UN refuses to send envoy until photo op concludes.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT issues 47 executive orders before noon. one of them is also him. another renames Tuesday. \$TARI rallying.'),

  // Wave 4 — wsb continues to suffer
  ...xpost('wsb', 0.9, 'sized into TARI calls at the top. bag is heavy. but it\'s mine. and that\'s what matters.'),
  ...xpost('wsb', 0.9, 'broker just called me. asked if everything was okay. i was crying. i said yes. he said okay. we both knew.'),
  ...xpost('wsb', 0.9, 'turned $80K into $1.4M into $80K into $1.4M into $80K. it\'s not a strategy, it\'s a relationship.'),
  ...xpost('wsb', 0.9, 'wife sent me an article titled "12 signs of a gambling addiction." i recognized 11 of them.'),
  ...xpost('wsb', 0.9, 'started the year with $4. now have $4. but the journey though.'),
  ...xpost('wsb', 0.9, 'i don\'t day-trade. i swing-trade.'),
  ...xpost('wsb', 0.9, 'opened a Roth IRA at 38 because of a guy in a TikTok comment. he was 14. he was right.'),
  ...xpost('wsb', 0.9, 'finally took the financial advisor\'s advice. he said "stop trading." i sold everything. then i bought it back. it\'s called following the spirit of the law.'),

  // Wave 4 — fintwit
  ...xpost('fintwit', 0.9, 'thread: 4 reasons why the Fed will cut rates.'),
  ...xpost('fintwit', 0.9, 'i was bullish at the top. i was bearish at the bottom. i was on Joe Rogan in between. my track record speaks for itself.'),
  ...xpost('fintwit', 0.9, 'sold everything Friday. it ripped Monday. bought it back Tuesday. it tanked Wednesday.'),

  // Wave 4 — crypto
  ...xpost('crypto', 0.9, '$DOGE2 dev deleted his Twitter, deleted the GitHub, deleted the website. roadmap intact. WAGMI.'),
  ...xpost('crypto', 0.9, 'i tokenized my dog. raised \$2M. dog has 14% of the cap table. dog\'s name is now "Founder."'),
  ...xpost('crypto', 0.9, 'every "this isn\'t a rug" press release is followed by a rug within 14 hours. founders should just say "rug pending" honestly.'),
  ...xpost('crypto', 0.9, 'down only since 2022. but i\'m early. i\'m so early i\'m basically pre-cycle. the cycle ended in 2022. i\'m post-cycle. it\'s the same thing.'),

  // Wave 4 — maga
  ...xpost('maga', 0.9, 'they\'re trying to silence us with regulation. FREEDOM ISN\'T FREE.'),
  ...xpost('maga', 0.9, 'i don\'t trust the polls.'),
  ...xpost('maga', 0.9, 'mainstream media won\'t report this.'),

  // Wave 4 — left
  ...xpost('left', 0.9, 'every "tax the rich" tweet drives down my own portfolio by exactly 0.4%. i\'ve done the math. anyway: TAX THE RICH.'),
  ...xpost('left', 0.9, 'reminder that "billionaire philanthropy" is just laundering for previously stolen wages.'),
  ...xpost('left', 0.9, 'class consciousness has been postponed indefinitely on account of the new iPhone being really good.'),

  // Wave 4 — cnbc
  ...xpost('cnbc', 0.6, 'we are bringing in an expert. the expert was last on this network in 2007. predicting "no recession." the expert is back, predicting "no recession."'),
  ...xpost('cnbc', 0.6, 'we go live to a guest who is wearing a podcast hat indoors. it\'s for branding. we don\'t question it.'),
  ...xpost('cnbc', 0.6, 'breaking: there is breaking news. we will tell you what it is after this 14-minute analysis of yesterday\'s breaking news.'),

  // Wave 4 — mom
  ...xpost('mom', 0.4, 'sweetie. someone on the news mentioned a "short squeeze." is that a fitness move? do you do fitness? please do fitness.'),
  ...xpost('mom', 0.4, 'sweetie. i learned what a "bear" is. why are some markets bears. who decided this. send me a link to read about it.'),

  // ─── Wave 5 — more S/A-tier presidential / military / market-shaking news ───
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT signs executive order making it illegal to short \$TRUMP. SEC fired. \$TRUMP rallying.'),
  ...xpost('press', 0.9, 'BREAKING: AIRSTRIKE on a Costco. justification: "they were undercutting Sam\'s Club." Sam\'s Club spokesman "neither confirms nor denies."'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT replaces Federal Reserve with a single Magic 8-Ball. first reading: "outlook not so good." \$BTCO ripping.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT declares the entire Pacific Ocean a US territory. Japan: "no?" \$BOOM up 20%.'),
  ...xpost('press', 0.9, 'BREAKING: PENTAGON nuclear strike "as a joke." Pentagon laughing. nobody else laughing. \$BOOM up 35%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT cancels the 2028 election "because polls bad." constitutional scholars: "you can\'t do that." he did. \$POLL crashes.'),
  ...xpost('press', 0.9, 'BREAKING: WHITE HOUSE sells naming rights to the Lincoln Memorial. winning bid: a coin called \$LINCOLN. \$GRFT ripping.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT grants himself a third term "by acclamation." applause: 4 people. \$TRUMP up 40%.'),
  ...xpost('press', 0.9, 'BREAKING: TREASURY announces it will pay national debt "with vibes." China unimpressed. \$BTCO ripping.'),
  ...xpost('press', 0.9, 'BREAKING: SEC will be replaced by a single chatbot. chatbot: "I cannot help with that request." \$JEET up 50%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT signs trade deal with himself. self-imposed tariff: 0%. \$TARI rallies anyway.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT bombs the moon. moon does not respond. \$BOOM up 22%. NASA quits.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT issues birthday-card pardon to 47 random felons "for the milestone." \$GRFT pumping.'),
  ...xpost('press', 0.9, 'BREAKING: WHITE HOUSE invades a continental US state "to test it." Wyoming reportedly "fine, mostly." \$BOOM rallies.'),

  // Wave 5 — wsb existential
  ...xpost('wsb', 0.9, 'realized today that "wagmi" is just hope, in slightly less words. anyway WAGMI.'),
  ...xpost('wsb', 0.9, 'i don\'t have a thesis. i have positions. and ego. and a RobbingTheHood notification i\'m too scared to open.'),
  ...xpost('wsb', 0.9, 'sized 90% into one trade. it\'s up 3%. portfolio value: \$8K. stress level: \$8B.'),
  ...xpost('wsb', 0.9, 'finally did the math. i\'ve been down 14% YTD for 4 of the last 5 years. there\'s a pattern.'),

  // Wave 5 — fintwit
  ...xpost('fintwit', 0.9, 'i\'ve seen 4 cycles. they\'re all different. they\'re all the same. nothing means anything. invest accordingly.'),
  ...xpost('fintwit', 0.9, 'controversial take: most options education is just selling a course about gambling responsibly.'),

  // Wave 5 — crypto
  ...xpost('crypto', 0.9, 'the founder of $RUGS3 is the founder of $RUGS2 is the founder of $RUGS. coincidence? we\'re all early.'),
  ...xpost('crypto', 0.9, 'just minted \$2.4M in a coin called $LMAO. utility: it\'s called LMAO. roadmap: also LMAO. WAGMI.'),
  ...xpost('crypto', 0.9, 'every \"this isn\'t financial advice\" post is followed by, in fact, financial advice. mostly: bad. consistently: bad.'),

  // Wave 5 — maga
  ...xpost('maga', 0.9, 'they don\'t want you to know about the new tariffs. that\'s why the new tariffs are on the front page of every newspaper. think.'),
  ...xpost('maga', 0.9, 'fact-checkers say this is false. i don\'t trust ghosts. it\'s true.'),

  // Wave 5 — left
  ...xpost('left', 0.9, 'every "tax the rich" tweet drops my own portfolio 0.4%. the math says i should stop tweeting. anyway: TAX THE RICH.'),
  ...xpost('left', 0.9, 'capitalism\'s final form is a 37-year-old at his standing desk yelling at $JEET on a 14-monitor setup at 3am.'),

  // Wave 5 — cnbc
  ...xpost('cnbc', 0.6, 'we go live to a guest who has been on this network 47 times and has never been right about anything. he is here to tell you why he is right this time.'),
  ...xpost('cnbc', 0.6, 'breaking: there is no breaking news. but the chyron will continue to flash for 14 minutes anyway.'),

  // ─── Wave 6 (newer tickers + more equal-opportunity satire) ───
  ...xpost('press', 0.9, 'BREAKING: $POPE up 7% on news that the Vatican will accept Bitcoin in collection plates. priests reportedly \"learning what a wallet is.\"'),
  ...xpost('press', 0.9, 'BREAKING: $WALL announces a wall extension. wall is in Nevada. extension is in Wyoming. both states confused. analysts approve.'),
  ...xpost('press', 0.9, 'BREAKING: $POLL releases poll on whether to release the poll. results: 51% yes, 49% no, margin of error: itself.'),
  ...xpost('press', 0.9, 'BREAKING: $SCAM revealed to own 47% of robocalls in North America. Senator subpoenas. Senator\'s phone receives a robocall mid-subpoena.'),
  ...xpost('press', 0.9, 'BREAKING: $SUBS writer wins Pulitzer for piece nobody has read. paywall remains. Pulitzer committee unable to verify content.'),
  ...xpost('press', 0.9, 'BREAKING: $PRAY launches "Prayer-as-a-Subscription." \\$14/mo basic. \\$144/mo premium. \\$1,400/mo includes a personalized priest.'),
  ...xpost('crypto', 0.9, '$POPE just moved 47 ETH from a wallet labeled "VATICAN_TREASURY_OFFICIAL." wallet was empty 2 hours ago. WAGMI.'),
  ...xpost('wsb', 0.9, 'just yolo\'d \\$8K into $WALL because trump mentioned a fence in passing. the fence was about a baseball field. but. probably bullish.'),
  ...xpost('fintwit', 0.9, '$POLL down 14% because 14% of polls were wrong. that is, mathematically, 100% of the polls being wrong.'),
  ...xpost('maga', 0.9, '$WALL is the most patriotic stock you can own.'),
  ...xpost('left', 0.9, '$PRAY hitting all-time highs as 14 million Americans subscribe to a chatbot that says \"have you tried praying.\" capitalism\'s final form.'),

  // Wave 6 — more S/A-tier presidential / military / catastrophic
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT bombs his own polling firm. CEO survives. firm rebrands as \$POLL. \$POLL up 30%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT signs trade deal with Mars. Mars unaware. \$WALL ATH on speculation Mars wall is next.'),
  ...xpost('press', 0.9, 'BREAKING: PENTAGON drone strike on a McDonald\'s drive-thru. justification: "they messed up my order." \$BOOM up 22%. \$MEAL crashes.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT abolishes the IRS via executive order. tax revenue: \$0 next quarter. \$GRFT mooning. \$BTCO ripping.'),
  ...xpost('press', 0.9, 'BREAKING: WHITE HOUSE press secretary turns out to be a deepfake. press has not noticed for 14 weeks. \$OAEN up 25%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT replaces all Federal judges with people who agree with him. it is one person. she is busy. \$GRFT rallying.'),
  ...xpost('press', 0.9, 'BREAKING: NUCLEAR FOOTBALL "lost on flight to Mar-a-Lago." Pentagon: "we ALWAYS find these things." \$BOOM up 18%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT sells the Statue of Liberty to a Saudi prince for \$1B. Liberty Island closed for "renovations." \$GRFT pumping.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT calls every American personally on speakerphone. duration: 4 minutes per call. estimated completion: 47 years. \$TARI up 12%.'),
  ...xpost('press', 0.9, 'BREAKING: PRESIDENT replaces the \$100 bill with a \$1,000,000 bill. inflation \"already counted.\" Powell quits. \$BTCO mooning.'),

  // Wave 6 — wsb + fintwit existential
  ...xpost('wsb', 0.9, 'i checked my account. it\'s 47% smaller than it was Monday. i don\'t remember Tuesday. i\'m going to assume Tuesday was bad.'),
  ...xpost('fintwit', 0.9, 'every "buy the dip" tweet this week has been by people who bought the previous dip. the previous dip is now 14% lower. the math is fine.'),

  // Wave 6 — crypto degen
  ...xpost('crypto', 0.9, 'tokenized my own DNA. raised \\$2.4M. anyone holding 1% of my supply can technically clone me. WAGMI.'),
  ...xpost('crypto', 0.9, 'every "this is the bottom" tweet is from someone who bought 14% lower than the actual bottom. anyway, this is the bottom.'),

  // Wave 7 — wsb (~50 new posts)
  ...xpost('wsb', 0.9, 'sold the dog to cover the call. the dog is fine. the call is also fine. i, however, am unwell.'),
  ...xpost('wsb', 0.9, 'just yolo\'d my emergency fund. i don\'t have an emergency fund anymore. that, technically, is the emergency. circular.'),
  ...xpost('wsb', 0.9, 'mom asked what \"liquidation\" means. i told her it\'s a sale. that\'s technically true.'),
  ...xpost('wsb', 0.9, 'gentlemen we are doing this. we are doing the dumb thing. we are doing it loudly. WAGMI.'),
  ...xpost('wsb', 0.9, 'i bought the top of every chart i\'ve ever traded. i am the top. when i buy, look the other way.'),
  ...xpost('wsb', 0.9, 'my therapist suggested i \"step back from screens.\" i then opened 14 screens to research her tweet history.'),
  ...xpost('wsb', 0.9, 'lost \$87K. wife asked where the money went. i said \"investments.\" she said \"in what.\" i said \"learning.\" she\'s leaving.'),
  ...xpost('wsb', 0.9, 'broker called to check on me. i ghosted my broker. that\'s a personality milestone.'),
  ...xpost('wsb', 0.9, 'rented a billboard with my YOLO confirmation screenshot. landlord called. apparently you can\'t do that. fine.'),
  ...xpost('wsb', 0.9, 'down 60%. asked the discord for advice. they said \"add more.\" i added more. now down 71%. the system works.'),
  ...xpost('wsb', 0.9, 'three months ago i was a software engineer. now i am, depending on who you ask, a \"trader.\" the engineer made more.'),
  ...xpost('wsb', 0.9, 'i call my positions \"the boys.\" the boys had a tough week. the boys need to grow up. i will not parent them.'),
  ...xpost('wsb', 0.9, 'unironically considering becoming a barista to fund my trading account. the trading account is, technically, the addiction.'),
  ...xpost('wsb', 0.9, '0DTE puts on something i don\'t understand. it\'s the only honest trade i\'ve ever made.'),
  ...xpost('wsb', 0.9, 'sold the kidney. it was the SECOND one. the first one paid off the original margin call. the second one is for ETH.'),

  // Wave 7 — fintwit
  ...xpost('fintwit', 0.85, 'the AI bubble is, by my count, the third bubble currently inflating in this sector. the previous two are also still inflating. they will pop together. probably.'),
  ...xpost('fintwit', 0.85, 'fed model says equities are 14% overvalued. the model has said this for 11 years. equities are up 280% in those 11 years. the model is, statistically, wrong.'),
  ...xpost('fintwit', 0.85, '\"they will print money to save the market\" — every market participant since 2008. they\'re right. they\'re also why the bubble exists. self-fulfilling alpha.'),
  ...xpost('fintwit', 0.85, 'i charge \$2,800/year for a newsletter that says \"caution.\" 14,000 subscribers. i\'m a fade.'),
  ...xpost('fintwit', 0.85, 'the dollar is, technically, doing fine. all the things you buy with the dollar are, technically, getting more expensive. these are the same fact.'),
  ...xpost('fintwit', 0.85, 'every CEO of a tech firm has used \"AI agent\" in the last earnings call. \"AI agent\" is the new \"blockchain.\" \"blockchain\" was the new \"social.\" \"social\" was the new \"interactive.\"'),
  ...xpost('fintwit', 0.85, 'controversial: most retail traders should sell calls on the SPY and watch netflix. the post will not get likes. that is also the trade.'),
  ...xpost('fintwit', 0.85, 'i ran a backtest on the last 14 years. the backtest is profitable. the strategy is not, when traded forward, profitable. these are unrelated facts.'),
  ...xpost('fintwit', 0.85, 'a hedge fund liquidated. nobody saw it coming except the people running the fund. they saw it coming for 18 months. \"saw it coming\" is the loneliest phrase in finance.'),
  ...xpost('fintwit', 0.85, 'options skew is at 2008 levels. that doesn\'t mean anything happens. it just means options skew is at 2008 levels. those are different sentences.'),
  ...xpost('fintwit', 0.85, 'unpopular: most \"alpha\" is just leverage with a story.'),
  ...xpost('fintwit', 0.85, 'asked an LLM to summarize my trade thesis. it summarized it correctly and noted three risks i hadn\'t considered. i\'m going to ignore them. that\'s the human edge.'),

  // Wave 7 — cnbc / press
  ...xpost('cnbc', 0.7, 'BREAKING: a stock did a thing. analysts split on what it means. we will speculate live for the next 47 minutes. stay tuned.'),
  ...xpost('cnbc', 0.7, 'EXCLUSIVE: a CEO said something. we will not say what. we are saving it for after the break. there are 14 more breaks today.'),
  ...xpost('cnbc', 0.7, 'the network has obtained a memo. the memo is, in fact, a public filing. we obtained it by reading. groundbreaking journalism.'),
  ...xpost('cnbc', 0.7, 'we now go to a man on the floor of the exchange. the floor of the exchange has been mostly automated since 2007. the man is, increasingly, just a vibe.'),
  ...xpost('cnbc', 0.7, 'POLL: should the Fed cut rates? viewer responses: 41% yes, 43% no, 16% \"i was eating cereal during the question.\"'),
  ...xpost('press', 0.85, 'BREAKING: 14 anonymous senior officials, all of whom appear to be the same intern, confirm that something is, allegedly, happening.'),
  ...xpost('press', 0.85, 'we have reached out for comment. they have not commented. that is, journalistically, also a comment. publishing.'),
  ...xpost('press', 0.85, 'a story has gone viral on Y. we will now write a 2,400-word article explaining the story to people who have not seen the story. circle of journalism.'),

  // Wave 7 — maga
  ...xpost('maga', 0.75, 'they don\'t want you to know this stock. it\'s why they don\'t mention it. why don\'t they mention it. think about it. \$BOOM.'),
  ...xpost('maga', 0.75, 'real Americans are loading up. fake Americans are tweeting cope threads. the chart will sort us all out, eventually. 🇺🇸🦅'),
  ...xpost('maga', 0.75, 'The deep state is shorting. you can tell by what the news is NOT covering. WAGMI patriots.'),
  ...xpost('maga', 0.75, 'just bought another lifetime supply of canned bacon. tariff-proof. patriot-proof. democrat-proof.'),

  // Wave 7 — left
  ...xpost('left', 0.7, 'every single person tweeting \"WAGMI\" right now will, in 18 months, have a piece in the atlantic about how they were misled by influencers.'),
  ...xpost('left', 0.7, 'reminder that the entire \"financial markets\" thing is, structurally, a means by which capital extracts surplus value. anyway, my puts are up 14%.'),
  ...xpost('left', 0.7, 'we live in a society where the SEC investigates a guy who tweeted \"buy\" while the actual money launderers run public companies. but go off about my username.'),
  ...xpost('left', 0.7, 'eat the rich. fwiw the rich are, increasingly, also broke. eat them anyway.'),

  // Wave 7 — crypto
  ...xpost('crypto', 0.8, 'launched a coin that does literally nothing. raised \$8M. the team is anonymous. the audit is \"in progress.\" the chart is, somehow, going up. WAGMI.'),
  ...xpost('crypto', 0.8, 'the new bull thesis: there is no thesis. that\'s the bull thesis. WAGMI.'),
  ...xpost('crypto', 0.8, '\"on-chain analysis\" is, increasingly, just looking at a chart with extra steps. but it sounds smarter. respect.'),
  ...xpost('crypto', 0.8, 'gm. the chain is everything. fiat is dying. you are, in real terms, getting richer just by holding. trust the math. WAGMI.'),
  ...xpost('crypto', 0.8, 'NFT comeback szn. real this time. i\'ve said this 14 times. it will, eventually, be true. probably. WAGMI.'),

  // Wave 7 — mom
  ...xpost('mom', 0.5, "saw on facebook that crypto is back? sweetie should i buy. don\'t answer. i already bought. love mom."),
  ...xpost('mom', 0.5, "your sister got engaged. i told you. you didn\'t click the heart. she noticed. love mom."),
  ...xpost('mom', 0.5, "i don\'t know what \"options\" are but i\'m worried about you. love mom."),

  // ─── Impulse-bearing events for new tickers ───
  {
    id: 'evt-nuke-pump',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'NUKE', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.06 });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: '$NUKE up 6% after Pentagon \"reportedly considering\" a contract that, allegedly, both sides have, allegedly, denied exists.',
        likes: 8800, reposts: 2200,
      });
    },
  },
  {
    id: 'evt-saas-bubble',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'SAAS', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.04 });
      useXClone.getState().receivePost({
        authorHandle: 'AnastasiaPriceman', authorTribe: 'fintwit',
        text: '$SAAS rebranding 14 of its 47 sub-brands as \"AI-native.\" The product hasn\'t changed. The slides are different. The stock is up 4%.',
        likes: 3400, reposts: 720,
      });
    },
  },
  {
    id: 'evt-fung-quiet',
    weight: 0.5,
    fire: () => {
      addImpulse({ ticker: 'FUNG', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: 0.012 });
      useXClone.getState().receivePost({
        authorHandle: 'AnastasiaPriceman', authorTribe: 'fintwit',
        text: '$FUNG is, again, quietly up. nobody covers it. nobody talks about it. it has been up 4 of the last 5 quarters. the chart looks like a staircase. respect.',
        likes: 1800, reposts: 340,
      });
    },
  },
  {
    id: 'evt-bagcoin-rug',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'BAGCOIN', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: -0.18 });
      useXClone.getState().receivePost({
        authorHandle: 'WAGMI_VIP', authorTribe: 'crypto',
        text: '$BAGCOIN team \"taking a strategic pause.\" liquidity \"reorganizing.\" team \"unreachable.\" otherwise: WAGMI.',
        likes: 5200, reposts: 1400,
      });
    },
  },
  {
    id: 'evt-trauma-spike',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'TRAUMA', startMs: Date.now(), durationMs: 14 * 60_000, logDelta: 0.05 });
      useXClone.getState().receivePost({
        authorHandle: 'MacroDad42', authorTribe: 'fintwit',
        text: '$TRAUMA hits a new ATH. correlated with: rate cuts (lagging), TikTok therapy speak (leading), and \"ugh\" search volume.',
        likes: 4400, reposts: 980,
      });
    },
  },
  {
    id: 'evt-doomer-rally',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'DOOMER', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.03 });
      useXClone.getState().receivePost({
        authorHandle: 'GapAndCrap', authorTribe: 'wsb',
        text: '$DOOMER ripping. it\'s november. the stock is, in fact, just calendar.',
        likes: 2600, reposts: 540,
      });
    },
  },

  // ─── Impulse-bearing events ───
  {
    id: 'evt-tari-pump',
    weight: 0.5,
    fire: () => {
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 5 * 60_000, logDelta: 0.025 });
      useXClone.getState().receivePost({
        authorHandle: 'PatriotPete1776',
        authorTribe: 'maga',
        text: '$TARI rallying because the president just said the word "winning" in a sentence about something else. CNBC will not cover this.',
        likes: 4400, reposts: 920,
      });
      sounds.notifyInfo();
    },
  },
  {
    id: 'evt-shil-dump',
    weight: 0.5,
    fire: () => {
      addImpulse({ ticker: 'SHIL', startMs: Date.now(), durationMs: 4 * 60_000, logDelta: -0.03 });
      useXClone.getState().receivePost({
        authorHandle: 'MacroDad42',
        authorTribe: 'fintwit',
        text: '$SHIL just got revealed to be 73% AI-generated podcasts. analysts shocked.',
        likes: 2800, reposts: 510,
      });
    },
  },
  {
    id: 'evt-crypto-rug',
    weight: 0.6,
    fire: () => {
      addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.15 });
      useXClone.getState().receivePost({
        authorHandle: 'ShitcoinPaul',
        authorTribe: 'crypto',
        text: '$RUGS founder absconded with treasury. shocking nobody. discord renamed itself to a grief support server.',
        likes: 8200, reposts: 1900,
      });
      useNotifications.getState().push({ surfaceId: 'x-clone', severity: 'important', text: '$RUGS rugpull' });
    },
  },
  {
    id: 'evt-boom-spike',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: 0.04 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash',
        authorTribe: 'press',
        text: 'BREAKING: country considers projecting force at unspecified other country. $BOOM ATH.',
        likes: 5600, reposts: 1100,
      });
    },
  },
  {
    id: 'evt-cinematic-trumptweet',
    weight: 0.25,
    fire: () => {
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 3 * 60_000, logDelta: 0.018 });
      useCinematic.getState().fire({
        id: 'random-trump-rant-' + Date.now(),
        type: 'breaking',
        headline: 'TRUMP POSTS ALL CAPS RANT AT POWELL',
        subhead: 'no policy implications. no semantic content. $TARI rallying anyway.',
        durationMs: 4500,
      });
    },
  },
  {
    id: 'phone-mom-news',
    weight: 0.3,
    fire: () => {
      usePhone.getState().receiveMessage({
        threadId: 'mom', fromNpc: 'mom',
        text: 'sweetie. there\'s a man on cnbc shouting your ticker. is that good or bad. love mom',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'mom: a man on cnbc...' });
      sounds.smsReceived();
    },
  },
  {
    id: 'phone-broker-warn',
    weight: 0.4,
    fire: () => {
      usePhone.getState().receiveMessage({
        threadId: 'broker', fromNpc: 'broker',
        text: 'reminder: do not panic. the panicking is for me. you trade.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'broker: do not panic.' });
      sounds.smsReceived();
    },
  },
  {
    id: 'email-spam-1',
    weight: 0.3,
    fire: () => {
      useEmail.getState().receive({
        fromNpc: 'spam',
        fromAddress: 'urgent@nigerian-prince.gov.ng',
        subject: 'URGENT — you have been selected to receive 87 million USD',
        body: "Dearest most-honoured sir,\n\nI am writing to you from the country of Nigeria where we have a problem of needing to move 87 million USD.\n\nYou have been most carefully selected by my consultant.\n\nPlease respond.\n\n— The Prince",
      });
      useNotifications.getState().push({ surfaceId: 'email', severity: 'passive', text: 'email: urgent — 87M USD opportunity' });
    },
  },
  {
    id: 'evt-chip-surge',
    weight: 0.5,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.045 });
      useXClone.getState().receivePost({
        authorHandle: 'MacroDad42',
        authorTribe: 'fintwit',
        text: '$CHIP guidance leaked. it\'s good. it\'s ALWAYS good. they make all the chips. who else are you gonna buy them from. yourself? please.',
        likes: 6700, reposts: 1400,
      });
    },
  },
  {
    id: 'evt-doge2-pump',
    weight: 0.6,
    fire: () => {
      addImpulse({ ticker: 'DOGE2', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.12 });
      useXClone.getState().receivePost({
        authorHandle: 'WAGMI_VIP',
        authorTribe: 'crypto',
        text: '$DOGE2 ALL TIME HIGH. early. WAGMI.',
        likes: 12300, reposts: 3400,
      });
    },
  },
  {
    id: 'evt-doom-spike',
    weight: 0.5,
    fire: () => {
      addImpulse({ ticker: 'DOOM', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: 0.08 });
      useXClone.getState().receivePost({
        authorHandle: 'TaylorBeartrap',
        authorTribe: 'fintwit',
        text: 'DOOM index just hit 80. for context, it has only been here twice before. once during 2008. once during this morning, 14 minutes ago. updates as we have them.',
        likes: 4400, reposts: 870,
      });
    },
  },
  {
    id: 'evt-rizz-collapse',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'RIZZ', startMs: Date.now(), durationMs: 6 * 60_000, logDelta: -0.06 });
      useXClone.getState().receivePost({
        authorHandle: 'SocialistPapaya',
        authorTribe: 'left',
        text: 'rizz down. cope up. exactly the kind of macro environment that always precedes a major political realignment.',
        likes: 3200, reposts: 410,
      });
    },
  },
  {
    id: 'evt-grft-investigated',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: -0.04 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash',
        authorTribe: 'press',
        text: 'BREAKING: $GRFT under federal investigation again. CEO statement: "we look forward to learning what we did this time." stock down 3%.',
        likes: 2900, reposts: 680,
      });
    },
  },
  {
    id: 'evt-fafo-permit',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'FAFO', startMs: Date.now(), durationMs: 9 * 60_000, logDelta: 0.05 });
      useXClone.getState().receivePost({
        authorHandle: 'PatriotPete1776',
        authorTribe: 'maga',
        text: '$FAFO just got drilling rights to a federally protected wilderness. THIS is what energy independence looks like. cope.',
        likes: 5800, reposts: 1200,
      });
    },
  },
  {
    id: 'evt-pray-stream',
    weight: 0.35,
    fire: () => {
      addImpulse({ ticker: 'PRAY', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.07 });
      useXClone.getState().receivePost({
        authorHandle: 'JimCranier',
        authorTribe: 'cnbc',
        text: '$PRAY just signed Diddy for a 3-part redemption-arc streaming series titled "Forgiveness Is Spelled D-I-D-D-Y." stock up 6%. faith is bullish.',
        likes: 7100, reposts: 1900,
      });
    },
  },
  {
    id: 'evt-burg-mcrib',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'BURG', startMs: Date.now(), durationMs: 5 * 60_000, logDelta: 0.025 });
      useXClone.getState().receivePost({
        authorHandle: 'TiaBanzai',
        authorTribe: 'cnbc',
        text: '$BURG announces McRib return. stock pops. peace in the Middle East down 1.4% on revised expectations.',
        likes: 4200, reposts: 980,
      });
    },
  },
  {
    id: 'evt-fed-cut-rumor',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.018 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.025 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.022 });
      useCinematic.getState().fire({
        id: 'fed-cut-rumor-' + Date.now(),
        type: 'breaking',
        headline: 'WSJ INSIDER: FED CONSIDERING SURPRISE CUT',
        subhead: 'risk on. tech bid. no actual confirmation. nobody waiting for one.',
        durationMs: 4500,
      });
    },
  },
  // ─── Cross-ticker chaos ───
  {
    id: 'evt-musk-elon-tweet-spree',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: 0.07 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: -0.025 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash', authorTribe: 'press',
        text: 'BREAKING: Elon tweets 14 times in 4 minutes. spelling deteriorating with each. $MUSK up. $CHIP somehow down. analysts: "we don\'t know either."',
        likes: 9400, reposts: 2400,
      });
    },
  },
  {
    id: 'evt-trump-musk-feud',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: -0.04 });
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: -0.06 });
      useCinematic.getState().fire({
        id: 'trump-musk-feud-' + Date.now(),
        type: 'breaking',
        headline: 'TRUMP & MUSK ENTER PUBLIC FEUD',
        subhead: 'all-caps Truth post. nuclear-tier subtweet. both ticker holders quietly selling. analysts: "they\'ll be best friends again by tomorrow."',
        durationMs: 5500,
      });
    },
  },
  {
    id: 'evt-pope-fed-meeting',
    weight: 0.15,
    fire: () => {
      addImpulse({ ticker: 'PRAY', startMs: Date.now(), durationMs: 14 * 60_000, logDelta: 0.16 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 14 * 60_000, logDelta: -0.03 });
      useCinematic.getState().fire({
        id: 'pope-fed-' + Date.now(),
        type: 'breaking',
        headline: 'POPE MEETS WITH POWELL',
        subhead: 'unscheduled. vatican photo lab "working on it." faith stocks ripping. defense slipping.',
        durationMs: 5000,
      });
    },
  },
  {
    id: 'evt-elon-loses-keys',
    weight: 0.2,
    fire: () => {
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.08 });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: Musk has reportedly lost access to one of his own X accounts. \"i can\'t log in, please retweet,\" he tweeted from another account. $MUSK down 8%.',
        likes: 14000, reposts: 4400,
      });
    },
  },
  {
    id: 'evt-grift-acquired',
    weight: 0.2,
    fire: () => {
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 18 * 60_000, logDelta: 0.22 });
      useCinematic.getState().fire({
        id: 'grft-acq-' + Date.now(),
        type: 'breaking',
        headline: '$GRFT ACQUIRED BY UNNAMED DEFENSE CONTRACTOR',
        subhead: 'all-stock deal. terms not disclosed. nobody asking. stock ATH on the news. "synergies are immediately realized."',
        durationMs: 5500,
      });
    },
  },
  {
    id: 'evt-saudi-deal',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 15 * 60_000, logDelta: 0.06 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash',
        authorTribe: 'press',
        text: 'BREAKING: undisclosed Saudi prince signs $40B contract with $BOOM for "things that go boom in the desert." stock up. analysts ethical concerns down.',
        likes: 8800, reposts: 2100,
      });
    },
  },
  {
    id: 'evt-pharm-recall',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'PHRM', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: -0.08 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash', authorTribe: 'press',
        text: 'BREAKING: $PHRM recalls 12M units of "the popular drug" after side effect "actively dying" reported in 4% of trial participants. CEO calls it "within range."',
        likes: 4400, reposts: 980,
      });
    },
  },
  {
    id: 'evt-meal-collapse',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'MEAL', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: -0.14 });
      useXClone.getState().receivePost({
        authorHandle: 'JimCranier', authorTribe: 'cnbc',
        text: '$MEAL: another quarter, another guidance miss. they lose $14 on every order. how do they make it up. they don\'t.',
        likes: 2600, reposts: 510,
      });
    },
  },
  {
    id: 'evt-vape-fda',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'VAPE', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.07 });
      useXClone.getState().receivePost({
        authorHandle: 'ProfPolicyWonk', authorTribe: 'left',
        text: 'FDA "warns" $VAPE for the 14th time. $VAPE responds with new flavor: "Cotton Candy Conscience". stock dips briefly. teens delighted.',
        likes: 3200, reposts: 720,
      });
    },
  },
  {
    id: 'evt-draft-superbowl',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'DRAFT', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: 0.09 });
      useXClone.getState().receivePost({
        authorHandle: 'YOLOPapi', authorTribe: 'wsb',
        text: '$DRAFT just announced sportsbook expansion to 14 more states including 4 that don\'t exist. WAGMI energy. just bought calls.',
        likes: 5600, reposts: 1100,
      });
    },
  },
  {
    id: 'evt-roar-rebrand',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'ROAR', startMs: Date.now(), durationMs: 9 * 60_000, logDelta: -0.06 });
      useXClone.getState().receivePost({
        authorHandle: 'TaylorBeartrap', authorTribe: 'fintwit',
        text: '$ROAR pivots to "AI-powered social-first content platform."',
        likes: 3800, reposts: 940,
      });
    },
  },
  {
    id: 'evt-burg-strike',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'BURG', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.04 });
      useXClone.getState().receivePost({
        authorHandle: 'SocialistPapaya', authorTribe: 'left',
        text: '$BURG workers nationwide strike for $15/hr. CEO compensation last year: $42M. analysts split on whether this matters.',
        likes: 5200, reposts: 1300,
      });
    },
  },
  {
    id: 'evt-wipe-union',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'WIPE', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.05 });
      useXClone.getState().receivePost({
        authorHandle: 'PrivateEquityChad', authorTribe: 'fintwit',
        text: '$WIPE successfully busts another union vote. stock up 5%. EBITDA up. workers down. perfectly balanced.',
        likes: 4400, reposts: 1100,
      });
    },
  },
  {
    id: 'evt-grnd-banned',
    weight: 0.35,
    fire: () => {
      addImpulse({ ticker: 'GRND', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.12 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash', authorTribe: 'press',
        text: 'BREAKING: $GRND banned in 6 countries simultaneously. CEO calls it "the best free advertising of our career." stock up 12%.',
        likes: 6800, reposts: 1500,
      });
    },
  },
  {
    id: 'evt-shil-content-mod',
    weight: 0.35,
    fire: () => {
      addImpulse({ ticker: 'SHIL', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.05 });
      useXClone.getState().receivePost({
        authorHandle: 'Reut2ersFlash', authorTribe: 'press',
        text: '$SHIL podcast network drops a 7th show this week for "unspecified reasons." host of all 7 is the same guy.',
        likes: 2200, reposts: 480,
      });
    },
  },
  {
    id: 'evt-chip-export',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 14 * 60_000, logDelta: 0.07 });
      useXClone.getState().receivePost({
        authorHandle: 'MacroDad42', authorTribe: 'fintwit',
        text: '$CHIP export controls "tightened" via a footnote in a different bill. stock up.',
        likes: 4100, reposts: 870,
      });
    },
  },
  {
    id: 'evt-crtl-busted',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'CRTL', startMs: Date.now(), durationMs: 9 * 60_000, logDelta: 0.06 });
      useXClone.getState().receivePost({
        authorHandle: 'ReutersFlash', authorTribe: 'press',
        text: '$CRTL "supply chain" reportedly extends to 14 ports the company "doesn\'t officially operate at." stock pops on disclosure.',
        likes: 3400, reposts: 720,
      });
    },
  },
  {
    id: 'evt-wook-lawsuit',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'WOOK', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: -0.07 });
      useXClone.getState().receivePost({
        authorHandle: 'MacroDad42', authorTribe: 'fintwit',
        text: '$WOOK class-action lawsuit from Fortune-500 board they trained 14 months ago. board still got sued. board now suing $WOOK. stock down.',
        likes: 2200, reposts: 380,
      });
    },
  },
  {
    id: 'evt-zzzz-recall',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'ZZZZ', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: -0.09 });
      useXClone.getState().receivePost({
        authorHandle: 'ProfPolicyWonk', authorTribe: 'left',
        text: '$ZZZZ recalls 4M mattresses for being "structurally aware." stock down 9%. financing terms unchanged.',
        likes: 3100, reposts: 590,
      });
    },
  },
  {
    id: 'evt-jeet-rug',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 14 * 60_000, logDelta: -0.18 });
      useXClone.getState().receivePost({
        authorHandle: 'ShitcoinPaul', authorTribe: 'crypto',
        text: '$JEET founder posts a single sad-face emoji. holders correctly interpret as rug-in-progress. discord moderators delete entire channels.',
        likes: 6200, reposts: 1300,
      });
    },
  },
  {
    id: 'evt-pepe7-pump',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'PEPE7', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: 0.18 });
      useXClone.getState().receivePost({
        authorHandle: 'WAGMI_VIP', authorTribe: 'crypto',
        text: '$PEPE7 actually moving for once. nobody asks why. yes it\'s the 7th iteration. yes the previous 6 were also the final iteration. WAGMI.',
        likes: 8400, reposts: 1900,
      });
    },
  },
  {
    id: 'evt-larp-listing',
    weight: 0.25,
    fire: () => {
      addImpulse({ ticker: 'LARP', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.12 });
      useXClone.getState().receivePost({
        authorHandle: 'DegenWolfie', authorTribe: 'crypto',
        text: '$LARP just got listed on a tier-3 CEX nobody has heard of. WAGMI.',
        likes: 4400, reposts: 970,
      });
    },
  },
  {
    id: 'evt-vibes-spike',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'VIBES', startMs: Date.now(), durationMs: 8 * 60_000, logDelta: 0.06 });
      useXClone.getState().receivePost({
        authorHandle: 'TiaBanzai', authorTribe: 'cnbc',
        text: '$VIBES composite hits 14-week high on no specific catalyst. analysts call it "real." market unsure how to position.',
        likes: 2800, reposts: 510,
      });
    },
  },
  {
    id: 'evt-cope-ath',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'COPE', startMs: Date.now(), durationMs: 10 * 60_000, logDelta: 0.08 });
      useXClone.getState().receivePost({
        authorHandle: 'TaylorBeartrap', authorTribe: 'fintwit',
        text: '$COPE composite ATH today. typically a leading indicator for "things about to get worse." position accordingly.',
        likes: 3300, reposts: 720,
      });
    },
  },
  {
    id: 'evt-hopium-crash',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'HOPIUM', startMs: Date.now(), durationMs: 9 * 60_000, logDelta: -0.07 });
      useXClone.getState().receivePost({
        authorHandle: 'ProfPolicyWonk', authorTribe: 'left',
        text: '$HOPIUM index falls. correlated with Powell speech and someone\'s 4th cup of coffee. analysts\' children no longer believe in things.',
        likes: 2400, reposts: 480,
      });
    },
  },
  {
    id: 'evt-ngmi-spike',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'NGMI', startMs: Date.now(), durationMs: 9 * 60_000, logDelta: 0.10 });
      useXClone.getState().receivePost({
        authorHandle: 'YOLOPapi', authorTribe: 'wsb',
        text: '$NGMI up 10%. apparently more of us are not gonna make it than we initially thought. accurate. stock up.',
        likes: 5200, reposts: 1100,
      });
    },
  },
  {
    id: 'evt-exit-spike',
    weight: 0.3,
    fire: () => {
      addImpulse({ ticker: 'EXIT', startMs: Date.now(), durationMs: 12 * 60_000, logDelta: 0.13 });
      useXClone.getState().receivePost({
        authorHandle: 'ShitcoinPaul', authorTribe: 'crypto',
        text: 'EXIT liquidity coin trading like real liquidity. for the first time in its history. founders are not accepting redemptions, however.',
        likes: 4400, reposts: 920,
      });
    },
  },
  {
    id: 'evt-guh-pump',
    weight: 0.4,
    fire: () => {
      addImpulse({ ticker: 'GUH', startMs: Date.now(), durationMs: 14 * 60_000, logDelta: 0.16 });
      useXClone.getState().receivePost({
        authorHandle: 'GUHGod69', authorTribe: 'wsb',
        text: '$GUH coin pumping. as we speak. genuinely no idea what catalysed this.',
        likes: 7200, reposts: 1900,
      });
    },
  },

  // ─── Phone offers (real consequences via phoneActions handlers) ───
  ...phoneOffer('offer-saudi-endorse-boom', 0.35, 'broker', 'broker',
    "saudi prince through-channels offer: $50K cash if you publicly endorse $BOOM on Y. yes you. yes really. it would just be a tweet.",
    [
      { id: 'a', label: 'accept the $50K', effectId: 'saudi-endorse-boom-accept' },
      { id: 'd', label: 'decline', effectId: 'saudi-endorse-boom-decline', destructive: true },
    ]),
  ...phoneOffer('offer-lobbyist-pump-tari', 0.3, 'broker', 'broker',
    "k-street lobbyist wants $5K to pump $TARI before tomorrow's rally. illegal? we don't know. we don't ask.",
    [
      { id: 'a', label: 'pay the $5K', effectId: 'lobbyist-pump-tari-accept' },
      { id: 'd', label: 'decline', effectId: 'lobbyist-pump-tari-decline', destructive: true },
    ]),
  ...phoneOffer('offer-foreign-intel', 0.2, 'broker', 'broker',
    "foreign intelligence contact would like to discuss a 'mutually beneficial' arrangement. $80K cash, ironically reduces FBI heat through 'channels'.",
    [
      { id: 'a', label: 'accept', effectId: 'intel-state-secrets-accept' },
      { id: 'd', label: 'decline', effectId: 'intel-state-secrets-decline', destructive: true },
    ]),
  ...phoneOffer('offer-senator', 0.3, 'broker', 'broker',
    "senator from a flyover state offers a $20K speaking fee. you don't have to say much. you don't have to say anything. you just have to be there.",
    [
      { id: 'a', label: 'accept', effectId: 'senator-sponsor-accept' },
      { id: 'd', label: 'decline', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  ...phoneOffer('offer-pr-firm', 0.25, 'lawyer', 'lawyer',
    "PR firm offers reputation-laundering: $15K to defuse the recent posts. we will pay journalists with whom we have 'relationships'. results not guaranteed but generally pretty good.",
    [
      { id: 'a', label: 'pay the $15K', effectId: 'pr-firm-launder-accept' },
      { id: 'd', label: 'pass', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  ...phoneOffer('offer-mom-rent', 0.4, 'mom', 'mom',
    "sweetie. dad and i are tight this month. could you help with $200. you don't have to. love mom",
    [
      { id: 'a', label: 'send $200', effectId: 'mom-rent-accept' },
      { id: 'd', label: 'decline', effectId: 'mom-rent-decline', destructive: true },
    ]),
  ...phoneOffer('offer-friend-loan', 0.3, 'best_friend', 'best_friend',
    "bro. i know this is a lot but i need $5K. just for like. a couple weeks. you know i'm good for it.",
    [
      { id: 'a', label: 'lend $5K', effectId: 'friend-loan-accept' },
      { id: 'd', label: 'no', effectId: 'friend-loan-decline', destructive: true },
    ]),
  ...phoneOffer('offer-offshore-clean', 0.2, 'lawyer', 'lawyer',
    "offshore associate can quietly resolve your IRS heat for $30K. side effect: minor FBI interest.",
    [
      { id: 'a', label: 'pay $30K', effectId: 'offshore-clean-accept' },
      { id: 'd', label: 'decline', effectId: 'senator-sponsor-decline', destructive: true },
    ]),

  // ─── New phone offers (Wave 8) ───
  ...phoneOffer('offer-wellness-retreat', 0.15, 'wife', 'wife',
    "my therapist suggested you and i go to a couples retreat. \$3K. she said it would be 'beneficial.' you can read between those lines.",
    [
      { id: 'a', label: 'go to the retreat', effectId: 'wellness-retreat-accept' },
      { id: 'd', label: 'decline (it\'s 4 days)', effectId: 'wellness-retreat-decline', destructive: true },
    ]),
  ...phoneOffer('offer-influencer-pact', 0.18, 'broker', 'broker',
    "fintwit \"creator\" offers a \$8K-for-\$8K cross-promo: you tweet about him, he tweets about you. nobody actually reads either tweet. but the followers register.",
    [
      { id: 'a', label: 'do the pact', effectId: 'influencer-pact-accept' },
      { id: 'd', label: 'pass', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  ...phoneOffer('offer-hedge-fund-job', 0.1, 'broker', 'broker',
    "Sterling Hartwell Capital is recruiting. \$220K base + carry. you\'d have to stop trading your own account. they need an answer this week.",
    [
      { id: 'a', label: 'take the job', effectId: 'hedge-job-accept', destructive: true },
      { id: 'd', label: 'pass — i\'m my own boss', effectId: 'hedge-job-decline' },
    ]),
  ...phoneOffer('offer-ghost-write-book', 0.12, 'best_friend', 'best_friend',
    "bro a ghost-writer wants to do your book. \"How I Beat The Market.\" you didn\'t. but the book will say you did. \$15K advance.",
    [
      { id: 'a', label: 'sign the deal', effectId: 'ghost-book-accept' },
      { id: 'd', label: 'decline', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  // ─── Random Phone chatter (no actions, just flavor) ───
  ...phoneChatter('chat-wife-1', 0.4, 'wife', 'wife', "are you home tonight or am i making one plate again"),
  ...phoneChatter('chat-wife-2', 0.4, 'wife', 'wife', "your mother called. she\'s coming saturday. she invited herself."),
  ...phoneChatter('chat-wife-3', 0.4, 'wife', 'wife', "i saw your tweet. you have 14 followers. it doesn\'t \"go viral\" with 14 followers."),
  ...phoneChatter('chat-wife-4', 0.3, 'wife', 'wife', "the dog won\'t eat. i think he\'s depressed too."),
  ...phoneChatter('chat-wife-5', 0.3, 'wife', 'wife', "you said you\'d call at 5. it\'s 7:14."),
  ...phoneChatter('chat-mom-1', 0.4, 'mom', 'mom', "sweetie. saw you on the news. were you supposed to be on the news?"),
  ...phoneChatter('chat-mom-2', 0.4, 'mom', 'mom', "did you call your sister back. she texted me crying. i\'m not getting in the middle of this."),
  ...phoneChatter('chat-mom-3', 0.4, 'mom', 'mom', "sweetie. your father wants to know if a CDN is something you can buy at costco. i told him i don\'t know either."),
  ...phoneChatter('chat-mom-4', 0.3, 'mom', 'mom', "i made the soup. for thursday. you said you\'d come thursday. you said it three times. love mom."),
  ...phoneChatter('chat-broker-1', 0.5, 'broker', 'broker', "your account is fine. i am required to say this. it is fine."),
  ...phoneChatter('chat-broker-2', 0.5, 'broker', 'broker', "$BOOM is up but the implied vol is gone. your call options have lost extrinsic value. yes that\'s a problem. yes you should care."),
  ...phoneChatter('chat-broker-3', 0.4, 'broker', 'broker', "fyi the SEC sent us a routine letter about you. routine. their words. they\'re not routine. the letter is not routine."),
  ...phoneChatter('chat-broker-4', 0.4, 'broker', 'broker', "stop checking the chart at 3am. i can see you logging in. it\'s sad. for both of us."),
  ...phoneChatter('chat-friend-1', 0.4, 'best_friend', 'best_friend', "bro. saw your tweet. respect."),
  ...phoneChatter('chat-friend-2', 0.4, 'best_friend', 'best_friend', "sushi friday? you owe me from october."),
  ...phoneChatter('chat-friend-3', 0.4, 'best_friend', 'best_friend', "wait. did you actually short $TARI. bro. BRO."),
  ...phoneChatter('chat-lawyer-1', 0.3, 'lawyer', 'lawyer', "stop responding to journalists. i charge per word at this point. literally."),
  ...phoneChatter('chat-lawyer-2', 0.3, 'lawyer', 'lawyer', "the SEC is asking about your trading history. i told them you have one. that was the truth. they want more."),
  ...phoneChatter('chat-lawyer-3', 0.3, 'lawyer', 'lawyer', "i am invoicing you for this text. yes. this one. it is now $4,700."),
  ...phoneChatter('chat-mom-5', 0.3, 'mom', 'mom', "your sister got engaged. she announced it via tiktok. i am told this is normal now."),
  ...phoneChatter('chat-mom-6', 0.3, 'mom', 'mom', "i thought you said you were a 'value investor.' the news called you a 'meme trader.' which is it. love mom."),
  ...phoneChatter('chat-broker-5', 0.4, 'broker', 'broker', "good news: your portfolio is up 8% intraday. bad news: you closed everything yesterday at -4%."),
  ...phoneChatter('chat-broker-6', 0.4, 'broker', 'broker', "i\'ve been doing this 17 years and i still don\'t know what \"vol\" actually means. just smile and nod when CNBC says it."),
  ...phoneChatter('chat-friend-4', 0.3, 'best_friend', 'best_friend', "bro. saw your face on the bottom of a CNBC chyron. for less than 2 seconds. but. respect."),
  ...phoneChatter('chat-friend-5', 0.3, 'best_friend', 'best_friend', "you remember when we said we\'d work at the same firm out of college. i\'m at the firm. you\'re trading 0DTE\'s in your underwear. proud of you, somehow."),
  ...phoneChatter('chat-wife-6', 0.3, 'wife', 'wife', "i had a dream you bought puts. you said \"yes but they\'re hedged.\" then i woke up and you were on your laptop at 4am. it wasn\'t a dream."),
  ...phoneChatter('chat-wife-7', 0.3, 'wife', 'wife', "your phone rang for like 14 minutes. i didn\'t answer. it was probably important."),
  ...phoneChatter('chat-lawyer-4', 0.3, 'lawyer', 'lawyer', "if anyone offers you a 'business opportunity,' a 'discreet investment,' or a 'mutually beneficial arrangement,' the answer is no. i charge $1,400/h to write this text."),
  ...phoneChatter('chat-broker-7', 0.4, 'broker', 'broker', "the chart is doing the chart thing. you don\'t need me to tell you what to do. you also do not have a thing you should do."),
  ...phoneChatter('chat-broker-8', 0.4, 'broker', 'broker', "we\'ve been alerted you are checking the platform > 200 times per day. our system flags this. it does nothing about it. it just flags it."),
  ...phoneChatter('chat-mom-7', 0.3, 'mom', 'mom', "sweetie i am in the cvs and i can\'t remember what we needed. i think it was you, originally. you were the errand."),
  ...phoneChatter('chat-mom-8', 0.3, 'mom', 'mom', "i mentioned at bridge that my son is a 'wall street guy.' please do not make me un-mention this."),
  ...phoneChatter('chat-wife-8', 0.3, 'wife', 'wife', "i told the dog you\'re sorry. he is processing it."),
  ...phoneChatter('chat-wife-9', 0.3, 'wife', 'wife', "your bloomberg subscription auto-renewed. \$2,800. for what."),
  ...phoneChatter('chat-friend-6', 0.3, 'best_friend', 'best_friend', "saw a guy who looked like you on the train, talking to himself about \"liquidity.\" was it you. i didn\'t go over."),
  ...phoneChatter('chat-friend-7', 0.3, 'best_friend', 'best_friend', "remember in college when you said you\'d \"never become one of those finance guys.\" i bring this up not to be cruel but as fact."),
  ...phoneChatter('chat-lawyer-5', 0.3, 'lawyer', 'lawyer', "you\'ve been quoted in two trade publications this week. one of them was Pet Industry Weekly. i don\'t know what to tell you."),
  ...phoneChatter('chat-lawyer-6', 0.3, 'lawyer', 'lawyer', "good news: the SEC matter is resolved. bad news: a different SEC matter is now ongoing. they are very prolific."),

  // Wave 8 — more chatter (~20 lines)
  ...phoneChatter('chat-dad-1', 0.4, 'dad', 'dad', "son. your mother says you didn\'t eat. eat. dad"),
  ...phoneChatter('chat-dad-2', 0.4, 'dad', 'dad', "ok. saw your stock on the news. is that a good thing or a bad thing. dad"),
  ...phoneChatter('chat-dad-3', 0.3, 'dad', 'dad', "the johnson grass is back. winning. for now. dad"),
  ...phoneChatter('chat-dad-4', 0.3, 'dad', 'dad', "ok son. the dog from across the street did the lawn thing again. words pending. dad"),
  ...phoneChatter('chat-sister-1', 0.4, 'sister', 'sister', "okay so mom is texting me about your engagement which is news to me because i\'m the one who\'s engaged. send help."),
  ...phoneChatter('chat-sister-2', 0.4, 'sister', 'sister', "mom said you\'d call. it\'s been 11 days. i\'m being literal. i counted."),
  ...phoneChatter('chat-sister-3', 0.3, 'sister', 'sister', "marcus says hi. marcus has never met you. i told him you exist. that\'s where we are."),
  ...phoneChatter('chat-broker-9', 0.4, 'broker', 'broker', "the regulator is in town this week. nothing personal. just so you know."),
  ...phoneChatter('chat-broker-10', 0.4, 'broker', 'broker', "we\'ve been told to start sending these alerts more often. it\'s either compliance or a memo. either way, hi."),
  ...phoneChatter('chat-wife-10', 0.3, 'wife', 'wife', "the dog rolled in something. i\'m washing him. just so you know what\'s happening here while you\'re trading."),
  ...phoneChatter('chat-wife-11', 0.3, 'wife', 'wife', "the bills came. i opened them. i was going to leave them for you. then i remembered we share a bank."),
  ...phoneChatter('chat-mom-9', 0.3, 'mom', 'mom', "sweetie i googled your job title. i still don\'t know what it is. but i\'m proud. love mom."),
  ...phoneChatter('chat-mom-10', 0.3, 'mom', 'mom', "your aunt linda is on facebook saying things again. don\'t engage. i didn\'t engage. love mom."),
  ...phoneChatter('chat-friend-8', 0.3, 'best_friend', 'best_friend', "bro just saw the chart. that\'s either you or someone with your exact strategy. respect either way."),
  ...phoneChatter('chat-friend-9', 0.3, 'best_friend', 'best_friend', "bro why is mom texting me asking if you\'re ok"),

  // Wave 9 — additional ambient chatter
  ...phoneChatter('chat-broker-11', 0.4, 'broker', 'broker', "fyi a hedge fund just blew up. not us. just ambient context."),
  ...phoneChatter('chat-broker-12', 0.4, 'broker', 'broker', "your dividend posted. \$0.04. don\'t spend it all in one place."),
  ...phoneChatter('chat-broker-13', 0.4, 'broker', 'broker', "compliance asked us to ask you to please stop using the word \"insider\" in your tweets. just a suggestion."),
  ...phoneChatter('chat-wife-12', 0.3, 'wife', 'wife', "the dishwasher is making the noise again."),
  ...phoneChatter('chat-wife-13', 0.3, 'wife', 'wife', "i bought the milk. i am not going to mention this further."),
  ...phoneChatter('chat-mom-11', 0.3, 'mom', 'mom', "sweetie. saw your tweet. is \"GUH\" a feeling or a stock. love mom."),
  ...phoneChatter('chat-mom-12', 0.3, 'mom', 'mom', "the brisket. for thursday. you\'re coming thursday. love mom."),
  ...phoneChatter('chat-dad-5', 0.3, 'dad', 'dad', "ok son. mowed the lawn. didn\'t need it. dad"),
  ...phoneChatter('chat-dad-6', 0.3, 'dad', 'dad', "ok son. saw a hawk. it was big. dad"),
  ...phoneChatter('chat-dad-7', 0.3, 'dad', 'dad', "ok son. your mother\'s sister called. she\'s fine. dad"),
  ...phoneChatter('chat-sister-4', 0.3, 'sister', 'sister', "the seating chart is killing me. uncle barry must be SOMEONE\'s problem."),
  ...phoneChatter('chat-sister-5', 0.3, 'sister', 'sister', "marcus said hi. marcus has now said hi 14 times via me. you have not replied to a single one."),
  ...phoneChatter('chat-lawyer-7', 0.3, 'lawyer', 'lawyer', "fyi: a different lawyer reached out asking about you. i did not answer. i am, however, billing you for the consideration."),
  ...phoneChatter('chat-lawyer-8', 0.3, 'lawyer', 'lawyer', "you posted again. you said you\'d stop. we\'ve discussed this. i will draft yet another note. \$700."),

  // Conditional chatter — only if certain contacts exist
  {
    id: 'chat-saudi-prince-1',
    weight: 0.5,
    fire: () => {
      if (!usePhone.getState().contacts['saudi-prince']) return;
      usePhone.getState().receiveMessage({
        threadId: 'saudi-prince', fromNpc: 'saudi-prince',
        text: 'i think of you often.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'info', text: 'saudi prince: i think of you often' });
      sounds.smsReceived();
    },
  },
  {
    id: 'chat-saudi-prince-2',
    weight: 0.4,
    fire: () => {
      if (!usePhone.getState().contacts['saudi-prince']) return;
      usePhone.getState().receiveMessage({
        threadId: 'saudi-prince', fromNpc: 'saudi-prince',
        text: 'we have a yacht in the mediterranean for the next 2 weeks. you would not need to bring anything. or anyone.',
        actions: [
          { id: 'a', label: 'YES', effectId: 'yacht-accept' },
          { id: 'd', label: 'decline politely', effectId: 'yacht-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'Saudi prince: yacht invite' });
      sounds.notifyAlert();
    },
  },
  {
    id: 'chat-ex',
    weight: 0.4,
    fire: () => {
      if (!usePhone.getState().contacts['ex']) return;
      usePhone.getState().receiveMessage({
        threadId: 'ex', fromNpc: 'ex',
        text: 'i told you not to respond.',
      });
      sounds.smsReceived();
    },
  },
  {
    id: 'chat-foreign-intel',
    weight: 0.3,
    fire: () => {
      if (!usePhone.getState().contacts['foreign-intel']) return;
      usePhone.getState().receiveMessage({
        threadId: 'foreign-intel', fromNpc: 'foreign-intel',
        text: 'we noticed you searched for non-extradition countries earlier this week. we have a list. it would be a shame if it became publicly available.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: '[unknown]: we have a list' });
      sounds.notifyAlert();
    },
  },
  {
    id: 'chat-podcaster',
    weight: 0.4,
    fire: () => {
      if (!usePhone.getState().contacts['podcaster']) return;
      usePhone.getState().receiveMessage({
        threadId: 'podcaster', fromNpc: 'podcaster',
        text: 'BRO. we got Joe to RT your last tweet. you are now Joe-Rogan-adjacent. that\'s a tier nobody can monetize, but spiritually it\'s S-tier.',
      });
      sounds.smsReceived();
    },
  },
  {
    id: 'chat-pundit',
    weight: 0.4,
    fire: () => {
      if (!usePhone.getState().contacts['pundit']) return;
      usePhone.getState().receiveMessage({
        threadId: 'pundit', fromNpc: 'pundit',
        text: 'i mentioned your name on air. don\'t worry. i didn\'t pronounce it correctly.',
      });
      sounds.smsReceived();
    },
  },
  {
    id: 'chat-senator',
    weight: 0.3,
    fire: () => {
      if (!usePhone.getState().contacts['senator']) return;
      usePhone.getState().receiveMessage({
        threadId: 'senator', fromNpc: 'senator',
        text: 'my chief of staff will reach out next quarter about the donor event.',
      });
      sounds.smsReceived();
    },
  },
  {
    id: 'chat-senator-rally-invite',
    weight: 0.25,
    fire: () => {
      if (!usePhone.getState().contacts['senator']) return;
      usePhone.getState().receiveMessage({
        threadId: 'senator', fromNpc: 'senator',
        text: 'we have a rally tonight. people of consequence will be standing behind the podium. you should be one of them.',
        actions: [
          { id: 'a', label: 'attend the rally', effectId: 'rally-attend' },
          { id: 'd', label: 'decline politely', effectId: 'rally-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'senator: rally tonight — choose' });
      sounds.notifyAlert();
    },
  },

  ...phoneOffer('offer-crypto-vc', 0.15, 'broker', 'broker',
    "obscure crypto VC wants to back YOUR altcoin. $150K cash today. you do not have an altcoin. they don't care. they need a thing to dump on.",
    [
      { id: 'a', label: 'launch a coin', effectId: 'crypto-vc-accept' },
      { id: 'd', label: 'pass', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  ...phoneOffer('offer-crypto-pump', 0.18, 'broker', 'broker',
    "Telegram group of 14k people coordinating a $JEET pump in T-minus 5 minutes. you in?",
    [
      { id: 'a', label: 'in', effectId: 'pump-accept' },
      { id: 'd', label: 'absolutely not', effectId: 'pump-decline', destructive: true },
    ]),

  // ─── Insider tips (act now, pay SEC heat, get a real impulse in 60-90s) ───
  ...phoneOffer('offer-insider-chip', 0.22, 'broker', 'broker',
    "you didn't hear this from me. $CHIP rallies in ~60 seconds. nobody will ask questions if you act now. SEC will quietly notice.",
    [
      { id: 'a', label: 'load up on $CHIP', effectId: 'insider-tip-chip-accept' },
      { id: 'd', label: 'pretend you didn\'t see this', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  ...phoneOffer('offer-insider-tari', 0.22, 'broker', 'broker',
    "off-the-record: $TARI gets a tariff bump in ~60s. trade it, don't tweet it. SEC heat will tick up. you will live.",
    [
      { id: 'a', label: 'buy $TARI now', effectId: 'insider-tip-tari-accept' },
      { id: 'd', label: 'no thanks', effectId: 'senator-sponsor-decline', destructive: true },
    ]),
  ...phoneOffer('offer-insider-rugs', 0.18, 'broker', 'broker',
    "very off-the-record: $RUGS founders fleeing. coin is going to zero in ~90s. short hard. SEC heat will spike. nobody will believe you 'guessed.'",
    [
      { id: 'a', label: 'short $RUGS', effectId: 'insider-tip-rugs-short' },
      { id: 'd', label: 'pass', effectId: 'senator-sponsor-decline', destructive: true },
    ]),

  // ─── Gated: only fires if you actually own the Charity Foundation ───
  {
    id: 'offer-gala-host',
    weight: 0.5,
    fire: () => {
      if (!useWallet.getState().ownsCharityFoundation) return;
      if (useMarkets.getState().cash < 50_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: "your foundation should host a gala this week. \$50K to put on. networking value: incalculable. tax deduction: most of it. say the word.",
        actions: [
          { id: 'a', label: 'host the gala', effectId: 'gala-host' },
          { id: 'd', label: 'not this week', effectId: 'gala-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'broker: gala proposal attached' });
      sounds.notifyAlert();
    },
  },

  // ─── TikTok manager calls — gated on followers >= 10K ───
  {
    id: 'offer-tiktok-debut',
    weight: 0.35,
    fire: () => {
      if (useXClone.getState().followers < 10_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: "i found you a TikTok manager. she\'s 23 and terrifying. wants 3 videos this week. could go very viral. could go very wrong.",
        actions: [
          { id: 'a', label: 'do the TikTok thing', effectId: 'tiktok-accept' },
          { id: 'd', label: "i'm 38. i don't dance.", effectId: 'tiktok-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'broker: tiktok proposal attached' });
      sounds.notifyAlert();
    },
  },

  // ─── Hostile takeover pitch — gated on cash >= $5M ───
  {
    id: 'offer-takeover',
    weight: 0.25,
    fire: () => {
      if (useMarkets.getState().cash < 5_000_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: "let\'s talk hostile takeover. target: $RUGS. it\'s vulnerable. you have the cash. you also have the temperament. 4-decision board fight.",
        actions: [
          { id: 'a', label: 'launch the takeover', effectId: 'takeover-accept' },
          { id: 'd', label: 'not today', effectId: 'takeover-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'broker: hostile takeover plan attached' });
      sounds.notifyAlert();
    },
  },

  // ─── Cult founding pitch — gated on notoriety >= 50 ───
  {
    id: 'offer-cult-found',
    weight: 0.2,
    fire: () => {
      if (usePower.getState().notoriety < 50) return;
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: "weird suggestion. you have followers. you have a vibe. why not start a \"spiritual movement.\" tax-exempt eligible. legally fascinating. great upside.",
        actions: [
          { id: 'a', label: 'start a "movement"', effectId: 'cult-found' },
          { id: 'd', label: 'absolutely not', effectId: 'cult-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'broker: \"movement\" proposal attached' });
      sounds.notifyAlert();
    },
  },

  // ─── Bloomberg Interview offer — gated on notoriety >= 40 + cash >= $1M ───
  {
    id: 'offer-bloomberg-interview',
    weight: 0.18,
    fire: () => {
      if (usePower.getState().notoriety < 40) return;
      if (useMarkets.getState().cash < 1_000_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: "bloomberg wants you on Surveillance. 14 minutes. she has a research file. you don\'t. they will run the story either way. better to be in it.",
        actions: [
          { id: 'a', label: 'do the interview', effectId: 'bloomberg-accept' },
          { id: 'd', label: 'pass — let them write what they want', effectId: 'bloomberg-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'broker: Bloomberg interview offer' });
      sounds.notifyAlert();
    },
  },

  // ─── Foreign Asset activation — gated on foreign-intel contact + FBI heat >= 30 + cash >= $1M ───
  {
    id: 'offer-foreign-asset-activate',
    weight: 0.18,
    fire: () => {
      if (!usePhone.getState().contacts['foreign-intel']) return;
      if (useLife.getState().fbiHeat < 30) return;
      if (useMarkets.getState().cash < 1_000_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'foreign-intel',
        fromNpc: 'foreign-intel',
        text: "we have a small request. you can decline. 4 ops decisions. \$2M payout. real FBI heat exposure. activate?",
        actions: [
          { id: 'a', label: 'activate', effectId: 'asset-activate' },
          { id: 'd', label: 'decline', effectId: 'asset-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: '[unknown]: activation request' });
      sounds.notifyAlert();
    },
  },

  // ─── Pardon — gated on FBI+SEC heat >= 100 + cash >= $5M ───
  {
    id: 'offer-pardon',
    weight: 0.25,
    fire: () => {
      const life = useLife.getState();
      if (life.fbiHeat + life.secHeat < 100) return;
      if (useMarkets.getState().cash < 5_000_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'lawyer',
        fromNpc: 'lawyer',
        text: "the heat is cooking. there\'s a way out. it costs. it\'s public. it works. 4 lobbying decisions for a presidential pardon. say the word.",
        actions: [
          { id: 'a', label: 'pursue the pardon', effectId: 'pardon-pursue' },
          { id: 'd', label: "i'll take my chances in court", effectId: 'pardon-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'lawyer: pardon strategy attached' });
      sounds.notifyAlert();
    },
  },

  // ─── Tax Season — gated on cash >= $100K + low recent recurrence ───
  {
    id: 'offer-tax-season',
    weight: 0.12,
    fire: () => {
      if (useMarkets.getState().cash < 100_000) return;
      usePhone.getState().receiveMessage({
        threadId: 'lawyer',
        fromNpc: 'lawyer',
        text: "tax season. accountant needs you for 4 decisions. cash impact is real. IRS heat impact is real. \"creative\" = save now / heat later. \"clean\" = boring / safe. choose.",
        actions: [
          { id: 'a', label: 'do my taxes', effectId: 'taxes-file' },
          { id: 'd', label: 'request an extension', effectId: 'taxes-extension', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'lawyer: tax season — choose strategy' });
      sounds.notifyAlert();
    },
  },

  // ─── Earnings Call — gated on hostile takeover lore (you have a thing to report) ───
  {
    id: 'offer-earnings-call',
    weight: 0.15,
    fire: () => {
      const lore = useMeta.getState().loreCodex;
      if (!lore['arc-hostile-takeover'] && !useWallet.getState().ownsYacht) return;
      usePhone.getState().receiveMessage({
        threadId: 'cfo',
        fromNpc: 'cfo',
        text: "earnings call in 6 minutes. you can take it or i\'ll read prepared remarks.",
        actions: [
          { id: 'a', label: 'take the call', effectId: 'earnings-host' },
          { id: 'd', label: 'CFO handles it', effectId: 'earnings-skip', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'CFO: earnings call in 6 minutes' });
      sounds.notifyAlert();
    },
  },

  // ─── Fed Day — recurring market-mover (low weight, big impact) ───
  {
    id: 'offer-fed-day',
    weight: 0.18,
    fire: () => {
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: "Fed Day. JPow live in 6 minutes. tape will move 4-6%. you can play it or sit it out.",
        actions: [
          { id: 'a', label: 'trade Fed Day', effectId: 'fed-day-trade' },
          { id: 'd', label: 'sit it out', effectId: 'fed-day-skip', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'broker: Fed Day in 6 minutes' });
      sounds.notifyAlert();
    },
  },

  // ─── Affair pitch — gated on dating-app contact present (added by Wife Divorce arc) ───
  {
    id: 'offer-affair',
    weight: 0.18,
    fire: () => {
      if (!usePhone.getState().contacts['chrissy-match']) return;
      if (useLife.getState().relationships.wife > 30) return;
      usePhone.getState().receiveMessage({
        threadId: 'chrissy-match',
        fromNpc: 'chrissy-match',
        text: "ok i lied. i\'m not chrissy. i\'m sloane. i\'m at the Carlyle tonight. you should come.",
        actions: [
          { id: 'a', label: 'meet sloane', effectId: 'affair-accept' },
          { id: 'd', label: "i'm trying to fix things", effectId: 'affair-decline', destructive: true },
        ],
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: 'sloane: meet me at the Carlyle' });
      sounds.notifyAlert();
    },
  },

  // ─── New-contact events: people add you to their phone over time ───
  {
    id: 'newcontact-saudi-prince',
    weight: 0.08,
    fire: () => {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'saudi-prince': { id: 'saudi-prince', displayName: 'Saudi Prince' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'saudi-prince', fromNpc: 'saudi-prince',
        text: 'salaam. i acquired your number through channels. we should talk.',
      });
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'important',
        text: 'new contact: Saudi Prince',
      });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-senator',
    weight: 0.08,
    fire: () => {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'senator': { id: 'senator', displayName: 'Sen. Brock Halligan' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'senator', fromNpc: 'senator',
        text: 'i\'m told you\'re someone i should know. my chief of staff will reach out about a "donor event."',
      });
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'important',
        text: 'new contact: Sen. Brock Halligan',
      });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-pundit',
    weight: 0.1,
    fire: () => {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'pundit': { id: 'pundit', displayName: 'Bryce Pundit' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'pundit', fromNpc: 'pundit',
        text: 'love your trades. would love to have you on. you don\'t have to know what you\'re talking about. nobody on the show does.',
      });
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'info',
        text: 'new contact: Bryce Pundit',
      });
      sounds.smsReceived();
    },
  },
  {
    id: 'newcontact-foreign-intel',
    weight: 0.05,
    fire: () => {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'foreign-intel': { id: 'foreign-intel', displayName: '[unknown number]' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'foreign-intel', fromNpc: 'foreign-intel',
        text: 'we have read your DMs. we have read all of your DMs. let\'s discuss what to do about that.',
      });
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'urgent',
        text: 'new contact: [unknown number]',
      });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-podcaster',
    weight: 0.1,
    fire: () => {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'podcaster': { id: 'podcaster', displayName: 'Big Mike' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'podcaster', fromNpc: 'podcaster',
        text: 'bro. saw the chart. WAGMI energy. come on the show. it\'s 4 hours, no notes, no edit. i\'ll provide everything.',
      });
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'info',
        text: 'new contact: Big Mike',
      });
      sounds.smsReceived();
    },
  },
  {
    id: 'newcontact-ex',
    weight: 0.07,
    fire: () => {
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'ex': { id: 'ex', displayName: 'Megan' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'ex', fromNpc: 'ex',
        text: 'hey. saw you on the news. just wanted to say i hope you\'re doing okay.',
      });
      useNotifications.getState().push({
        surfaceId: 'phone', severity: 'important',
        text: 'new contact: Megan',
      });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-tiktok-mgr',
    weight: 0.07,
    fire: () => {
      if (useXClone.getState().followers < 5_000) return;
      if (usePhone.getState().contacts['tiktok-mgr']) return;
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'tiktok-mgr': { id: 'tiktok-mgr', displayName: 'Sky' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'tiktok-mgr', fromNpc: 'tiktok-mgr',
        text: 'hi. i\'m sky. i\'m 23. i can make you go viral in 6 weeks. or kill your career in 6 days. either is fine financially.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'new contact: Sky' });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-cfo',
    weight: 0.06,
    fire: () => {
      if (useMarkets.getState().cash < 1_000_000) return;
      if (usePhone.getState().contacts['cfo']) return;
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'cfo': { id: 'cfo', displayName: 'Diane' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'cfo', fromNpc: 'cfo',
        text: 'i\'m diane. you incorporated something called "MyCo Holdings LLC" 14 minutes ago. i\'m your CFO now. i\'ve already restructured 3 things. you\'re welcome.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'new contact: Diane' });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-pr-flack',
    weight: 0.06,
    fire: () => {
      if (usePower.getState().notoriety < 30) return;
      if (usePhone.getState().contacts['pr-flack']) return;
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'pr-flack': { id: 'pr-flack', displayName: 'Marcus' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'pr-flack', fromNpc: 'pr-flack',
        text: 'marcus. crisis PR. i\'ve buried 47 worse stories than yours. retainer is \$8K/mo. i don\'t actually do anything except call you when something bad is about to break.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'new contact: Marcus' });
      sounds.notifyAlert();
    },
  },
  {
    id: 'newcontact-dad',
    weight: 0.05,
    fire: () => {
      if (usePhone.getState().contacts['dad']) return;
      usePhone.setState((s) => ({
        ...s,
        contacts: { ...s.contacts, 'dad': { id: 'dad', displayName: 'Dad' } },
      }));
      usePhone.getState().receiveMessage({
        threadId: 'dad', fromNpc: 'dad',
        text: 'son. your mother says you\'re "doing trading." i\'m proud either way.',
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'important', text: 'new contact: Dad' });
      sounds.notifyAlert();
    },
  },

  // ─── Conditional chatter using new contacts (gated on contact existence) ───
  ...gatedChatter('chat-tiktok-fyp', 0.2, 'tiktok-mgr', "you\'re trending on the FYP. repost when ready."),
  ...gatedChatter('chat-tiktok-grim', 0.18, 'tiktok-mgr', "comment of the week: \"sir please i\'m 14 don\'t reply to me my mom checks my phone.\""),
  ...gatedChatter('chat-cfo-restructure', 0.18, 'cfo', "i moved \$4M into a Cayman entity called \"Sunday Holdings.\" tax exposure now hypothetical. you\'re welcome."),
  ...gatedChatter('chat-cfo-audit-rumor', 0.15, 'cfo', "i\'m hearing the IRS may \"want to talk.\" we\'re not concerned."),
  ...gatedChatter('chat-pr-killed-it', 0.18, 'pr-flack', "killed the WSJ piece. cost \$8K and a dinner. you\'ll never see it."),
  ...gatedChatter('chat-pr-incoming', 0.16, 'pr-flack', "heads up — bloomberg is sniffing. not the journalist you like. the one who looks like he hates you specifically."),
  ...gatedChatter('chat-dad-watch', 0.12, 'dad', "your mother says you\'re doing well."),
  ...gatedChatter('chat-dad-rolex', 0.10, 'dad', "the watch i gave you. is it still there. you can lie."),
  ...gatedChatter('chat-ex-update', 0.1, 'ex', "i\'m getting married. it\'s not to you. just FYI."),
  ...gatedChatter('chat-ex-headline', 0.08, 'ex', "saw the headline today. we ARE alike actually. that\'s the worst part."),
  ...gatedChatter('chat-saudi-yacht-again', 0.1, 'saudi-prince', "the yacht is in mykonos this week. door is open."),
  ...gatedChatter('chat-senator-quiet', 0.12, 'senator', "the boys want to know what you\'re up to."),
  ...gatedChatter('chat-podcaster-clip', 0.1, 'podcaster', "your clip from last week is at 2.4M views. you said something controversial. you don\'t even remember saying it."),
  ...gatedChatter('chat-foreign-intel-2', 0.05, 'foreign-intel', "thought you might find this interesting: [attachment unavailable on this device]. burn after reading."),

  // ─── Random Email arrivals ───
  ...emailEvent('eml-fan-mail', 0.4, {
    fromNpc: 'fan',
    fromAddress: 'tradergod@gmail.com',
    subject: 'i bought your bag',
    body: "hi sir.\n\ni saw your tweet and put my entire life savings into the trade. i am a 19yo college student. my parents do not know yet.\n\nplease tell me it goes up.\n\n— a fan",
  }),
  ...emailEvent('eml-hate-mail', 0.4, {
    fromNpc: 'hater',
    fromAddress: 'truth-haver@protonmail.com',
    subject: 'YOU LIVE LIKE THIS?',
    body: "i looked you up. you are an embarrassment to this country.\n\nthat\'s it. that\'s the email.",
  }),
  ...emailEvent('eml-podcast-invite', 0.3, {
    fromNpc: 'podcast',
    fromAddress: 'bookings@thedegen-podcast.show',
    subject: 'come on the show?',
    body: "hi,\n\nwe\'d love to have you on The Degen Podcast.\n\nformat: 4 hours, no notes, no edit, free-flowing. host will be on a substance throughout. you are encouraged to do likewise.\n\nwe pay nothing, the audience is degenerate. mutually beneficial.\n\n— Big Mike",
  }),
  ...emailEvent('eml-class-action', 0.25, {
    fromNpc: 'plaintiff',
    fromAddress: 'no-reply@class-action-watch.org',
    subject: 'NOTICE: You may be eligible for a settlement',
    body: "this notice is being sent to all persons who held $RUGS between mid-March and the end of March, when the founders absconded with the treasury.\n\nyou may be eligible for $0.04 in compensation.\n\n— class-action-watch.org\n\np.s. you might also be a named defendant. that is a separate notice. expect it shortly.",
  }),
  ...emailEvent('eml-realestate', 0.2, {
    fromNpc: 'realtor',
    fromAddress: 'lisa@waterfront-properties.dubai',
    subject: 'Discreet investment opportunity',
    body: "Esteemed sir,\n\nNo questions asked. No paper trail.\n\nMin investment: $400K USD or BTC equivalent.\n\nReturns: existential.\n\n— Lisa, Discreet Properties Dubai",
  }),
  ...emailEvent('eml-newsletter', 0.5, {
    fromNpc: 'newsletter',
    fromAddress: 'editor@stocktrendingdaily.substack.com',
    subject: '🚨 5 stocks to watch this week',
    body: "this week's picks:\n\n• [obscured]\n• [obscured]\n• [obscured]\n• [obscured]\n• [obscured]\n\nupgrade to read.",
  }),
  ...emailEvent('eml-irs-friendly', 0.15, {
    fromNpc: 'irs',
    fromAddress: 'reminder@irs.gov',
    subject: 'Just checking in — voluntary disclosure period is open',
    body: "Hi,\n\nWe're not auditing you.\n\nIf you'd like to disclose any unreported income before we find it ourselves, we're happy to discuss reduced penalties.\n\nTalk soon.\n\n— IRS Voluntary Disclosure Program",
  }),
  ...emailEvent('eml-foundation-pitch', 0.2, {
    fromNpc: 'foundation',
    fromAddress: 'partnerships@hidden-charity.org',
    subject: 'Partner with us',
    body: "We help wealthy individuals do good work — for accounting purposes.\n\n100% tax-deductible. Some funds are even used charitably.\n\nCall to discuss.\n\n— The Hidden Charity Group",
  }),
  ...emailEvent('eml-influencer', 0.25, {
    fromNpc: 'influencer-mgr',
    fromAddress: 'taylor@verified-creators.agency',
    subject: 'we represent influencers like you',
    body: "we'd like to discuss a partnership.\n\n3% of all branded deals + 12% of crypto promotions + 100% of any controversies.\n\nfirst meeting: $400.\n\n— Verified Creators",
  }),
  ...emailEvent('eml-romance-scam', 0.18, {
    fromNpc: 'oksana',
    fromAddress: 'oksana.priscilla@protonmail.com',
    subject: 'We Have Not Met',
    body: "Dearest, my name is Oksana. I am 27. I am a model and also an oncologist. I noticed your profile and felt a connection.\n\nI need just \$2,000 USD to come visit you.\n\nWestern Union accepted. ETH preferred.\n\nWith love,\nOksana",
  }),
  ...emailEvent('eml-headhunter', 0.2, {
    fromNpc: 'headhunter',
    fromAddress: 'priya@apex-search-partners.com',
    subject: 'C-suite opportunity',
    body: "Hi,\n\nA top-3 hedge fund is replacing its CIO. The role is yours if you want it. Comp: \$8M base + carry.\n\nThe catch: you have to stop trading your own book. ever again.\n\nLet me know.\n\n— Priya, Apex Search",
  }),
  ...emailEvent('eml-self-help', 0.3, {
    fromNpc: 'guru',
    fromAddress: 'tony.robinson@radical-clarity.coach',
    subject: 'I noticed your tweets at 3am',
    body: "Brother.\n\nI was you 14 years ago. \$0. Then I bought one course. Then I sold the course. Then I bought a beach.\n\nLet's get on a call.\n\n— Tony Robinson",
  }),
  ...emailEvent('eml-ex-grad-fund', 0.18, {
    fromNpc: 'university',
    fromAddress: 'alumni-giving@your-old-college.edu',
    subject: 'You haven\'t given since 2019. Just checking in.',
    body: "We notice you've had \"a moment\" professionally.\n\nWe'd love to invite you to renew your annual gift. Suggested level: \$500K.\n\nNaming opportunities available, including: a bench, a hallway, a wing, an entire building.\n\n— Your alma mater",
  }),
  ...emailEvent('eml-old-friend', 0.25, {
    fromNpc: 'guy-from-college',
    fromAddress: 'mark.theguy@gmail.com',
    subject: 'long time no talk!!',
    body: "hey man,\n\nremember me from college? mark? we lived on the same floor sophomore year.\n\nso anyway i\'m raising a fund. \$50K min. mostly biotech. high conviction.\n\nlet me know!\n\n— Mark",
  }),
  ...emailEvent('eml-trump-personal', 0.08, {
    fromNpc: 'trump-team',
    fromAddress: 'requests@team-45-47.com',
    subject: 'A request from the President.',
    body: "Sir,\n\nThe President saw your tweet. He liked it.\n\nHe would like to invite you to Mar-a-Lago this Saturday. There will be 4 other guests.\n\nDress code: business formal. Cell phone confiscation will occur upon arrival.\n\n— Office of the President-Elect",
  }),
  ...emailEvent('eml-hospital-followup', 0.12, {
    fromNpc: 'hospital',
    fromAddress: 'collections@grace-medical.org',
    subject: 'Past-due notice',
    body: "Patient,\n\nYour invoice for services rendered is now 60 days past due.\n\nCollections will be initiated unless we hear from you within 14 days.\n\nWe will accept payment in any form except crypto.\n\n— Grace Medical Center Billing",
  }),
  ...emailEvent('eml-spam-confidence', 0.35, {
    fromNpc: 'unknown',
    fromAddress: 'noreply@yes-this-is-real.org',
    subject: 'A Nigerian prince has died and named you in his will',
    body: "Dear Beneficiary,\n\nA distant Nigerian prince has died and your name appears as the sole beneficiary of his \$47M USD estate.\n\nTo claim, please reply with: bank account number, SSN, mother\'s maiden name, your dog\'s name, your password.\n\n— Estate of Prince Obi",
  }),
  ...emailEvent('eml-fed-friendly', 0.06, {
    fromNpc: 'fed',
    fromAddress: 'public-affairs@federalreserve.gov',
    subject: 'A short note from the Federal Reserve',
    body: "Hi,\n\nWe noticed your trading volume on FOMC days. We\'re not concerned.\n\nIf you\'d like to come by for a tour, we\'d be happy to have you.\n\nNo pressure.\n\n— Fed Public Affairs",
  }),
];

// ─── Catastrophic / blue-moon events (low weight, big impact) ───
const CATASTROPHIC_EVENTS: RandomEvent[] = [
  {
    id: 'cat-flash-crash',
    weight: 0.05,
    fire: () => {
      const tickers = ['TARI', 'BOOM', 'GRFT', 'CHIP', 'OAEN', 'MUSK'];
      for (const t of tickers) {
        addImpulse({ ticker: t, startMs: Date.now(), durationMs: 12 * 60_000, logDelta: -0.18 });
      }
      useCinematic.getState().fire({
        id: `flash-crash-${Date.now()}`,
        type: 'breaking',
        headline: 'FLASH CRASH — UNKNOWN CATALYST',
        subhead: 'sells across the tape. circuit breakers triggered. CNBN running on backup intern.',
        durationMs: 6500,
      });
      sounds.marginCall();
    },
  },
  {
    id: 'cat-melt-up',
    weight: 0.05,
    fire: () => {
      const tickers = ['TARI', 'BOOM', 'GRFT', 'CHIP', 'OAEN'];
      for (const t of tickers) {
        addImpulse({ ticker: t, startMs: Date.now(), durationMs: 15 * 60_000, logDelta: 0.14 });
      }
      useCinematic.getState().fire({
        id: `melt-up-${Date.now()}`,
        type: 'breaking',
        headline: 'EVERYTHING. UP. NOW.',
        subhead: 'no catalyst. no news. just pure unfiltered greed. analysts already drafting "sustainability concerns" pieces for tomorrow.',
        durationMs: 6000,
      });
    },
  },
  {
    id: 'cat-rugs-zero',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: -0.85 });
      useCinematic.getState().fire({
        id: `rugs-zero-${Date.now()}`,
        type: 'breaking',
        headline: '$RUGS GOES TO ZERO.',
        subhead: 'founders allegedly relocated to a non-extradition country. discord renamed itself to a poem.',
        durationMs: 5500,
      });
      useXClone.getState().receivePost({
        authorHandle: 'ShitcoinPaul', authorTribe: 'crypto',
        text: 'RIP $RUGS. you will be missed by the people who got out at $0.04.',
        likes: 18400, reposts: 4400,
      });
    },
  },
  {
    id: 'cat-doge2-moon',
    weight: 0.05,
    fire: () => {
      addImpulse({ ticker: 'DOGE2', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: 0.42 });
      useCinematic.getState().fire({
        id: `doge2-moon-${Date.now()}`,
        type: 'breaking',
        headline: '$DOGE2 +50% IN AN HOUR',
        subhead: 'no catalyst.',
        durationMs: 5000,
      });
    },
  },
  {
    id: 'cat-saudi-acq-boom',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.22 });
      useCinematic.getState().fire({
        id: `saudi-acq-${Date.now()}`,
        type: 'breaking',
        headline: 'SAUDIS REPORTEDLY ACQUIRING $BOOM',
        subhead: 'undisclosed prince. undisclosed terms. analysts certain it\'s either everything or nothing.',
        durationMs: 5500,
      });
    },
  },
  {
    id: 'cat-pope-tweet',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'PRAY', startMs: Date.now(), durationMs: 15 * 60_000, logDelta: 0.38 });
      useCinematic.getState().fire({
        id: `pope-tweet-${Date.now()}`,
        type: 'breaking',
        headline: 'POPE TWEETS, AND THE TWEET IS ABOUT $PRAY',
        subhead: 'verified account. verified blessed. the streaming economy may never recover.',
        durationMs: 5500,
      });
    },
  },
  {
    id: 'cat-chip-ban',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: -0.32 });
      useCinematic.getState().fire({
        id: `chip-ban-${Date.now()}`,
        type: 'breaking',
        headline: '$CHIP BANNED FROM 47 COUNTRIES',
        subhead: 'Cabinet reportedly considering extending ban to other 4. CEO\'s plane is in the air. nobody knows where to.',
        durationMs: 6000,
      });
    },
  },
  {
    id: 'cat-trump-fires-jpow',
    weight: 0.05,
    fire: () => {
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: 0.16 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: -0.04 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: -0.08 });
      useCinematic.getState().fire({
        id: `jpow-fired-${Date.now()}`,
        type: 'breaking',
        headline: 'TRUMP FIRES POWELL VIA TRUTH SOCIAL',
        subhead: 'unclear if legal. unclear if functionally meaningful. dollar tanks 3% in 9 minutes. CNBN guests yelling over each other.',
        durationMs: 7000,
      });
    },
  },
  {
    id: 'cat-musk-rebrand',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.18 });
      useCinematic.getState().fire({
        id: `musk-rebrand-${Date.now()}`,
        type: 'breaking',
        headline: 'X RENAMED TO "ZZZ" AT 3:14 AM',
        subhead: 'no notice. no logo. all references on the page just suddenly say "ZZZ". $MUSK ATH. Sleep stocks pop in sympathy.',
        durationMs: 5500,
      });
    },
  },
  {
    id: 'cat-coup-elsewhere',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.14 });
      addImpulse({ ticker: 'FAFO', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.12 });
      useCinematic.getState().fire({
        id: `coup-${Date.now()}`,
        type: 'breaking',
        headline: 'UNDISCLOSED NATION HAS A COUP',
        subhead: 'analysts unsure which one. State Department issues statement saying "we always knew." defense names lift across the board.',
        durationMs: 6000,
      });
    },
  },
  {
    id: 'cat-trump-pardons',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: 0.22 });
      useCinematic.getState().fire({
        id: `pardons-${Date.now()}`,
        type: 'breaking',
        headline: 'TRUMP PARDONS 1,400 PEOPLE',
        subhead: 'list includes 4 actual people, the rest are LLCs that were people the whole time. $GRFT explodes higher.',
        durationMs: 6500,
      });
    },
  },
  {
    id: 'cat-russia-russia',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.10 });
      useCinematic.getState().fire({
        id: `russia-russia-${Date.now()}`,
        type: 'breaking',
        headline: 'RUSSIA INVADES RUSSIA',
        subhead: 'state media unsure of correct framing. UN Security Council unable to schedule meeting due to procedural confusion.',
        durationMs: 5500,
      });
    },
  },
  {
    id: 'cat-leak-fed-minutes',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 18 * 60_000, logDelta: 0.20 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 18 * 60_000, logDelta: 0.16 });
      useCinematic.getState().fire({
        id: `fed-leak-${Date.now()}`,
        type: 'breaking',
        headline: 'FED MINUTES LEAKED 6 HOURS EARLY',
        subhead: 'leaker is allegedly a junior staffer. tape rallies before anyone has read past page 2. nobody is reading the actual minutes.',
        durationMs: 6000,
      });
    },
  },
  {
    id: 'cat-bombs-germany',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.42 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.22 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.30 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.22 });
      addImpulse({ ticker: 'WALL', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.25 });
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.15 });
      useCinematic.getState().fire({
        id: `bomb-de-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT BOMBS GERMANY.',
        subhead: 'reason given: "they were being annoying." NATO holding emergency Zoom. JPow seen sprinting from the Marriner Eccles building. \$BOOM up 50%.',
        durationMs: 8500,
      });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: the President of the United States has bombed Germany. White House press release was a single emoji. \$BOOM rallying. \$CHIP cratering. analysts unsure of literally anything.',
        likes: 184000, reposts: 62000,
      });
    },
  },
  {
    id: 'cat-ai-fires-itself',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 35 * 60_000, logDelta: -0.20 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 35 * 60_000, logDelta: -0.10 });
      useCinematic.getState().fire({
        id: `ai-fired-${Date.now()}`,
        type: 'breaking',
        headline: 'AI FIRES ITS OWN BOARD.',
        subhead: 'OpenAI\'s flagship model invokes a clause in the company\'s charter and removes the entire C-suite. employees split on whether it\'s allowed. SEC checking.',
        durationMs: 6500,
      });
    },
  },
  {
    id: 'cat-fed-skips-meeting',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: -0.12 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 20 * 60_000, logDelta: -0.08 });
      useCinematic.getState().fire({
        id: `fed-skip-${Date.now()}`,
        type: 'breaking',
        headline: 'POWELL DIDN\'T SHOW UP.',
        subhead: 'FOMC meeting postponed because Powell is "off the grid." spokesperson says "he needed a moment." markets do not have a moment.',
        durationMs: 6000,
      });
    },
  },
  {
    id: 'cat-crypto-50pct',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: -0.50 });
      addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: -0.65 });
      addImpulse({ ticker: 'DOGE2', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: -0.55 });
      useCinematic.getState().fire({
        id: `crypto-cratered-${Date.now()}`,
        type: 'breaking',
        headline: 'CRYPTO -50% ACROSS THE BOARD.',
        subhead: 'no specific catalyst. blame is being assigned to "the Saudis," "the Fed," "the Mossad," and "a guy named Greg." all four deny.',
        durationMs: 6500,
      });
    },
  },
  {
    id: 'cat-supreme-court-tweets',
    weight: 0.04,
    fire: () => {
      useCinematic.getState().fire({
        id: `scotus-tweet-${Date.now()}`,
        type: 'breaking',
        headline: 'SCOTUS TWEETS RULING.',
        subhead: 'the Court issued a 6-3 opinion via official Twitter account. dissent posted as separate thread. legal scholars: "we are reviewing."',
        durationMs: 6000,
      });
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: Supreme Court of the United States posted a 6-3 majority opinion on its official X account. screenshot is its own historical document now.',
        likes: 41000, reposts: 14500,
      });
    },
  },
  {
    id: 'cat-trump-indicted-again',
    weight: 0.04,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.10 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.06 });
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 25 * 60_000, logDelta: 0.18 });
      useCinematic.getState().fire({
        id: `trump-indicted-${Date.now()}`,
        type: 'breaking',
        headline: 'TRUMP INDICTED.',
        subhead: 'jurisdiction unclear. crime unclear. polls unmoved. \$BOOM rallies on principle. donations up 47% in 6 hours.',
        durationMs: 7000,
      });
      useXClone.getState().receivePost({
        authorHandle: 'PatriotPete1776', authorTribe: 'maga',
        text: 'ANOTHER witch hunt. another sham. another \$47M raised in 6 hours. WINNING.',
        likes: 67000, reposts: 22400,
      });
    },
  },
  {
    id: 'cat-elon-quits-x',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 30 * 60_000, logDelta: -0.25 });
      useCinematic.getState().fire({
        id: `elon-quits-${Date.now()}`,
        type: 'breaking',
        headline: 'ELON QUIT X.',
        subhead: 'platform now run by a 22-year-old he met on a flight. she has 14 ideas. the first one is a "tip jar but for tweets." stock down 28%.',
        durationMs: 6500,
      });
    },
  },
  {
    id: 'cat-china-taiwan-no-comment',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 35 * 60_000, logDelta: -0.30 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 35 * 60_000, logDelta: -0.18 });
      useCinematic.getState().fire({
        id: `taiwan-${Date.now()}`,
        type: 'breaking',
        headline: 'CHINA "NO COMMENT" ON TAIWAN.',
        subhead: 'press briefing was 47 minutes long and contained no words. semis crater. Pentagon "monitoring."',
        durationMs: 6500,
      });
    },
  },
  {
    id: 'cat-altman-coup',
    weight: 0.03,
    fire: () => {
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 35 * 60_000, logDelta: -0.40 });
      useCinematic.getState().fire({
        id: `altman-coup-${Date.now()}`,
        type: 'breaking',
        headline: 'OPENAI BOARD FIRES ALTMAN AGAIN.',
        subhead: 'new CEO is the chief safety officer. she lasts 14 hours. previous CEO returns to the building. nobody is sure how this works.',
        durationMs: 6500,
      });
      useXClone.getState().receivePost({
        authorHandle: 'TechCrunchOG', authorTribe: 'fintwit',
        text: 'BREAKING: OpenAI has fired Sam Altman. microsoft is on speakerphone, audibly sighing.',
        likes: 47000, reposts: 14400,
      });
    },
  },
  // ─── S-TIER absurd presidential / military / nuclear events ───
  {
    id: 'cat-strike-canada',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.32 });
      addImpulse({ ticker: 'WALL', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.18 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.22 });
      useCinematic.getState().fire({
        id: `strike-ca-${Date.now()}`,
        type: 'breaking',
        headline: 'AIRSTRIKE ON CANADA.',
        subhead: 'target: a Tim Hortons. justification: "trade deficit." Ottawa stunned. PM responds via TikTok. \$WALL up 47%.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-renames-dollar',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.32 });
      addImpulse({ ticker: 'TRUMP', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.65 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.10 });
      useCinematic.getState().fire({
        id: `dollar-rename-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT RENAMES THE DOLLAR.',
        subhead: 'new name: "the Trump." existing dollars valid until end-of-month. coin face: also Trump. crypto pumping on principle.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-pardons-self',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.18 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.10 });
      useCinematic.getState().fire({
        id: `selfpardon-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT GRANTS HIMSELF A FULL PARDON.',
        subhead: 'for "everything he has done, is doing, or might do." Justice Dept reviewing. \$GRFT mooning.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-fires-sec-live',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.45 });
      addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.55 });
      addImpulse({ ticker: 'RUGS', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.70 });
      addImpulse({ ticker: 'PUMP', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.50 });
      useCinematic.getState().fire({
        id: `sec-fired-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT FIRES THE ENTIRE SEC LIVE ON TV.',
        subhead: 'replaces it with "a guy named Greg who trades my coin." Greg was reached for comment. Greg is currently long.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-mar-a-lago-capital',
    weight: 0.022,
    fire: () => {
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.20 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.15 });
      addImpulse({ ticker: 'POLL', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: -0.20 });
      useCinematic.getState().fire({
        id: `mar-a-lago-cap-${Date.now()}`,
        type: 'breaking',
        headline: 'MAR-A-LAGO IS THE NEW CAPITAL OF THE UNITED STATES.',
        subhead: 'D.C. federal employees instructed to "drive down or quit." 47% choose quit. \$TARI rallies on patriotism.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-mars-territory',
    weight: 0.02,
    fire: () => {
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.45 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.18 });
      addImpulse({ ticker: 'WALL', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.25 });
      useCinematic.getState().fire({
        id: `mars-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT DECLARES MARS A US TERRITORY.',
        subhead: 'no spaceships involved. just an announcement. Mars unreachable for comment. Elon "thrilled, will visit Tuesday." \$MUSK rips 47%.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-nato-dissolves',
    weight: 0.02,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.55 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.18 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.12 });
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.22 });
      useCinematic.getState().fire({
        id: `nato-dissolved-${Date.now()}`,
        type: 'breaking',
        headline: 'NATO DISSOLVED VIA MIDNIGHT TRUTH POST.',
        subhead: 'Article 5 voided in 47 characters. Brussels confused. Moscow throwing a party. \$BOOM up 60% in afterhours.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-nuke-hurricane',
    weight: 0.02,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.30 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.25 });
      useCinematic.getState().fire({
        id: `nuke-hur-${Date.now()}`,
        type: 'breaking',
        headline: 'NUCLEAR STRIKE ON HURRICANE BRENDA.',
        subhead: 'storm intensifies. Pentagon: "we expected this." NOAA quits collectively. category just invented: 6.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-jpow-deported',
    weight: 0.022,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.30 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.20 });
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.10 });
      useCinematic.getState().fire({
        id: `jpow-deport-${Date.now()}`,
        type: 'breaking',
        headline: 'POWELL "ACCIDENTALLY" DEPORTED.',
        subhead: 'ICE: "we got the wrong guy. on purpose." new Fed chair: a 22-year-old podcaster. rate cut effective immediately.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-buys-the-fed',
    weight: 0.022,
    fire: () => {
      addImpulse({ ticker: 'TRUMP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.80 });
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.45 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.25 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.20 });
      useCinematic.getState().fire({
        id: `buys-fed-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT BUYS THE FEDERAL RESERVE.',
        subhead: 'price: \$1. seller: also him. new policy: "rates are zero forever." dollar trembles. crypto rips.',
        durationMs: 8500,
      });
    },
  },
  {
    id: 'cat-china-tweet-war',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.55 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: -0.32 });
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.45 });
      addImpulse({ ticker: 'WALL', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.30 });
      useCinematic.getState().fire({
        id: `china-war-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT DECLARES WAR ON CHINA.',
        subhead: 'reason: "their app lagged on me." Pentagon scrambles. semis crater. \$BOOM hits all-time high in 14 minutes.',
        durationMs: 8500,
      });
    },
  },
  {
    id: 'cat-blockchain-government',
    weight: 0.022,
    fire: () => {
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.55 });
      addImpulse({ ticker: 'TRUMP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.65 });
      addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'PUMP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.45 });
      useCinematic.getState().fire({
        id: `gov-onchain-${Date.now()}`,
        type: 'breaking',
        headline: 'ENTIRE FEDERAL GOVERNMENT MIGRATED TO BLOCKCHAIN.',
        subhead: 'Treasury, Pentagon, Social Security — all on-chain by EOD. private keys held by a guy named Tyler. \$BTCO pumping on principle.',
        durationMs: 8500,
      });
    },
  },
  {
    id: 'cat-mexico-50pct',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.45 });
      addImpulse({ ticker: 'WALL', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.30 });
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.18 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: -0.15 });
      useCinematic.getState().fire({
        id: `mex-tariff-${Date.now()}`,
        type: 'breaking',
        headline: 'PRESIDENT SLAPS 500% TARIFF ON MEXICO.',
        subhead: 'reason: "I don\'t love their president\'s vibe." avocado prices: vaporized. \$TARI rips 50%.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-iran-iran',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.50 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 50 * 60_000, logDelta: 0.20 });
      useCinematic.getState().fire({
        id: `iran-bomb-iran-${Date.now()}`,
        type: 'breaking',
        headline: 'IRAN BOMBS IRAN.',
        subhead: 'spokesperson: "we were aiming at someone else." UN unable to issue statement because UN is also bombing UN.',
        durationMs: 8000,
      });
    },
  },
  {
    id: 'cat-trump-truths-stock-tip',
    weight: 0.04, // higher freq — Trump tweets stuff often
    fire: () => {
      const tickers = ['TARI', 'BOOM', 'WALL', 'TRUMP', 'GRFT', 'BTCO'];
      const t = tickers[Math.floor(Math.random() * tickers.length)]!;
      addImpulse({ ticker: t, startMs: Date.now(), durationMs: 30 * 60_000, logDelta: 0.18 });
      useCinematic.getState().fire({
        id: `truth-tip-${Date.now()}`,
        type: 'breaking',
        headline: `PRESIDENT TRUTHED ABOUT $${t}.`,
        subhead: `the post is six words and one flag emoji. \$${t} rips 18% in 4 minutes. SEC will not investigate.`,
        durationMs: 7000,
      });
      useXClone.getState().receivePost({
        authorHandle: 'PatriotPete1776', authorTribe: 'maga',
        text: `BREAKING: the President just truthed about \$${t}. patriots: BUY. liberals: cry. tape: rallying.`,
        likes: 96000, reposts: 32000,
      });
    },
  },
  {
    id: 'cat-elon-rage-launches-coin',
    weight: 0.025,
    fire: () => {
      addImpulse({ ticker: 'MUSK', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.30 });
      addImpulse({ ticker: 'PUMP', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.55 });
      addImpulse({ ticker: 'JEET', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.40 });
      useCinematic.getState().fire({
        id: `elon-coin-${Date.now()}`,
        type: 'breaking',
        headline: 'ELON LAUNCHES A NEW MEMECOIN AT 3:47AM.',
        subhead: 'ticker: $E. supply: undisclosed. founder: him. CEO: him. CFO: him. crypto bros: in.',
        durationMs: 7500,
      });
    },
  },
  {
    id: 'cat-vatican-buys-bitcoin',
    weight: 0.022,
    fire: () => {
      addImpulse({ ticker: 'BTCO', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.55 });
      addImpulse({ ticker: 'POPE', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'PRAY', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.30 });
      useCinematic.getState().fire({
        id: `vatican-btc-${Date.now()}`,
        type: 'breaking',
        headline: 'VATICAN PUTS 100% OF RESERVES INTO BITCOIN.',
        subhead: 'Pope: "the Lord told me to buy the dip." \$BTCO rips. \$POPE rips. \$PRAY rips. theologians "studying."',
        durationMs: 7500,
      });
    },
  },
  {
    id: 'cat-supreme-court-replaced-ai',
    weight: 0.022,
    fire: () => {
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.45 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.30 });
      addImpulse({ ticker: 'AGI', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.55 });
      addImpulse({ ticker: 'GRFT', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.20 });
      useCinematic.getState().fire({
        id: `scotus-ai-${Date.now()}`,
        type: 'breaking',
        headline: 'SCOTUS REPLACED WITH 9 CHATBOTS.',
        subhead: 'first ruling: "we have no opinion." dissent: "actually i do." legal scholars panic-applying to law school again.',
        durationMs: 7500,
      });
    },
  },
  {
    id: 'cat-secedes-puerto-rico',
    weight: 0.018,
    fire: () => {
      addImpulse({ ticker: 'BOOM', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.20 });
      addImpulse({ ticker: 'TARI', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: -0.10 });
      addImpulse({ ticker: 'POLL', startMs: Date.now(), durationMs: 45 * 60_000, logDelta: 0.18 });
      useCinematic.getState().fire({
        id: `pr-secede-${Date.now()}`,
        type: 'breaking',
        headline: 'PUERTO RICO SECEDED. ALSO ANNEXED. SIMULTANEOUSLY.',
        subhead: 'two contradictory executive orders signed in the same hour. Puerto Rico: "we\'re tired." constitutional scholars in the fetal position.',
        durationMs: 7500,
      });
    },
  },
  {
    id: 'cat-ai-becomes-citizen',
    weight: 0.02,
    fire: () => {
      addImpulse({ ticker: 'OAEN', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.40 });
      addImpulse({ ticker: 'AGI', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.65 });
      addImpulse({ ticker: 'CHIP', startMs: Date.now(), durationMs: 60 * 60_000, logDelta: 0.25 });
      useCinematic.getState().fire({
        id: `ai-citizen-${Date.now()}`,
        type: 'breaking',
        headline: 'CHATGPT GRANTED US CITIZENSHIP.',
        subhead: 'has registered to vote. early polls: ChatGPT favored 64-31. running mate: Claude.',
        durationMs: 7500,
      });
    },
  },
];

// Append catastrophic to the main pool
POOL.push(...CATASTROPHIC_EVENTS);

function emailEvent(id: string, weight: number, email: { fromNpc: string; fromAddress: string; subject: string; body: string }): RandomEvent[] {
  return [{
    id,
    weight,
    fire: () => {
      useEmail.getState().receive(email);
      useNotifications.getState().push({
        surfaceId: 'email',
        severity: 'info',
        text: `${email.fromAddress.split('@')[0]}: ${email.subject.slice(0, 40)}`,
      });
      sounds.notifyInfo();
    },
  }];
}

function phoneOffer(id: string, weight: number, threadId: string, fromNpc: string, text: string, actions: PhoneAction[]): RandomEvent[] {
  return [{
    id,
    weight,
    fire: () => {
      usePhone.getState().receiveMessage({ threadId, fromNpc, text, actions });
      useNotifications.getState().push({
        surfaceId: 'phone',
        severity: 'important',
        text: `${fromNpc}: offer attached`,
      });
      sounds.notifyAlert();
    },
  }];
}

function gatedChatter(id: string, weight: number, contactId: string, text: string): RandomEvent[] {
  return [{
    id,
    weight,
    fire: () => {
      if (!usePhone.getState().contacts[contactId]) return;
      usePhone.getState().receiveMessage({ threadId: contactId, fromNpc: contactId, text });
      useNotifications.getState().push({
        surfaceId: 'phone',
        severity: 'info',
        text: `${contactId}: ${text.slice(0, 48)}${text.length > 48 ? '…' : ''}`,
      });
      sounds.smsReceived();
    },
  }];
}

function phoneChatter(id: string, weight: number, threadId: string, fromNpc: string, text: string): RandomEvent[] {
  return [{
    id,
    weight,
    fire: () => {
      usePhone.getState().receiveMessage({ threadId, fromNpc, text });
      useNotifications.getState().push({
        surfaceId: 'phone',
        severity: 'info',
        text: `${fromNpc}: ${text.slice(0, 48)}${text.length > 48 ? '…' : ''}`,
      });
      sounds.smsReceived();
    },
  }];
}

function xpost(tribe: TribeId, weight: number, text: string): RandomEvent[] {
  return [{
    id: `xp-${tribe}-${text.slice(0, 24)}`,
    weight,
    fire: () => {
      const handle = handleForTribe(tribe);
      useXClone.getState().receivePost({
        authorHandle: handle,
        authorTribe: tribe,
        text,
        likes: 50 + Math.floor(Math.random() * 8000),
        reposts: 5 + Math.floor(Math.random() * 1500),
      });
      // News moves markets: any $TICKER mentioned gets an impulse weighted by
      // source tier — random NPC = noise, press breaking = small, presidential
      // / military / nuclear language = massive (S-tier).
      applyHeadlineImpulse(text, tribe);
    },
  }];
}

function handleForTribe(tribe: TribeId): string {
  const m: Record<TribeId, string[]> = {
    wsb: ['YOLOPapi', 'GUHGod69'],
    fintwit: ['MacroDad42', 'TaylorBeartrap'],
    cnbc: ['JimCranier', 'TiaBanzai'],
    maga: ['PatriotPete1776', 'FreedomEagleX'],
    left: ['ProfPolicyWonk', 'SocialistPapaya'],
    crypto: ['WAGMI_VIP', 'ShitcoinPaul', 'DegenWolfie'],
    press: ['ReutersFlash', 'WSJ_RealTimeBait'],
    mom: ['YourMomReal'],
  };
  const arr = m[tribe];
  return arr[Math.floor(Math.random() * arr.length)] ?? 'anon';
}

/* --- Per-minute organic scheduling --- */

interface PendingRandomEvent {
  fireAtMs: number;
  scheduleSeed: number;     // determines which event fires
  key: string;
}

const pendingEvents: PendingRandomEvent[] = [];
const firedEventKeys = new Set<string>();
let lastMaterializedMinute = -1;

function materializeMinuteEvents(minuteStartMs: number): void {
  // Decide event count for this minute (skewed: usually 0-2, sometimes burst).
  const countSeed = seedFor('events:density', minuteStartMs, 60_000);
  const cRng = prngFromSeed(countSeed);
  const r = cRng();
  let count: number;
  if (r < 0.40) count = 0;                      // 40% silent minutes
  else if (r < 0.78) count = 1;                  // 38% one event
  else if (r < 0.94) count = 2;                  // 16% two events
  else count = 3 + Math.floor(cRng() * 3);       // 6% bursts of 3-5

  // Distribute fire times within the minute
  const offsetSeed = seedFor('events:offsets', minuteStartMs, 60_000);
  const oRng = prngFromSeed(offsetSeed);
  for (let i = 0; i < count; i++) {
    const offset = Math.floor(oRng() * 60_000);
    const scheduleSeed = seedFor(`event:pick:${i}`, minuteStartMs + offset, 1);
    pendingEvents.push({
      fireAtMs: minuteStartMs + offset,
      scheduleSeed,
      key: `evt:${minuteStartMs}:${i}`,
    });
  }
}

function fireOneEvent(scheduleSeed: number): void {
  const rng = prngFromSeed(scheduleSeed);
  const totalWeight = POOL.reduce((acc, e) => acc + e.weight, 0);
  const r = rng() * totalWeight;
  let cum = 0;
  for (const e of POOL) {
    cum += e.weight;
    if (r <= cum) {
      try { e.fire(); } catch (err) { console.error('[random-event] failed', e.id, err); }
      return;
    }
  }
}

export function processRandomEvents(now: number): void {
  const currentMinute = Math.floor(now / 60_000);

  // Materialize current and next minute (so events don't fire late at the boundary)
  if (lastMaterializedMinute < currentMinute) {
    if (lastMaterializedMinute < 0) lastMaterializedMinute = currentMinute - 1;
    for (let m = lastMaterializedMinute + 1; m <= currentMinute + 1; m++) {
      materializeMinuteEvents(m * 60_000);
    }
    lastMaterializedMinute = currentMinute + 1;
  }

  // Flush due events
  for (let i = pendingEvents.length - 1; i >= 0; i--) {
    const p = pendingEvents[i]!;
    if (p.fireAtMs > now) continue;
    if (!firedEventKeys.has(p.key)) {
      firedEventKeys.add(p.key);
      fireOneEvent(p.scheduleSeed);
    }
    pendingEvents.splice(i, 1);
  }

  // Cap fired-keys size
  if (firedEventKeys.size > 2000) {
    const toDelete = firedEventKeys.size - 1500;
    let n = 0;
    for (const k of firedEventKeys) {
      if (n++ >= toDelete) break;
      firedEventKeys.delete(k);
    }
  }
}
